import React, { useEffect, useState, useMemo } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { getCompanyLogoCandidates } from "../utils/getCompanyLogo";
import { db, auth } from "../firebase";
import { collection, doc, deleteDoc, getDocs, getDoc, limit, orderBy, query, setDoc, where, updateDoc } from "firebase/firestore";
import { hasCompanyInResumeExperiences } from "../utils/resumeParser";
import { listReviewsByCompanySlug } from "../services/reviews";
import { listCompanies, enrichCompanyWithBrasilAPI } from "../services/companies";
import { getUserRole, isPremium, isAdmin } from "../utils/rbac";
import { resolveProfileId } from "../utils/profileIdentity";
import { isUserProfileCertified } from "../utils/verificationLevel";
import { filterOutTestApoiadores } from "../utils/testAccounts";
import { handleCheckout } from "../services/billing";
import AppHeader from "../components/AppHeader";
import ConflictDeclarationGate from "../components/ConflictDeclarationGate";
import RelevantWorkersSection from "../components/RelevantWorkersSection";
import SelectionProcessOverview from "../components/SelectionProcessOverview";
// import PremiumPieCard from "../components/PremiumPieCard"; // removido pois não é mais usado
// ...existing code...

function normalizeKey(value) {
  return (value || "")
    .toString()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim();
}

function toSlug(value) {
  return (value || "")
    .toString()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+/g, "")
    .replace(/-+$/g, "");
}

const AUTO_MODERATION_TERMS = [
  "racista",
  "nazista",
  "estupr",
  "amea",
  "matar",
  "suicid",
  "porno",
  "xingamento",
];

function detectAutoModeration(rawText) {
  const text = (rawText || "").toString().trim();
  const normalized = normalizeKey(text);

  if (!normalized) {
    return { status: "review", reasons: ["conteudo_vazio"] };
  }

  const reasons = [];

  const hasBlockedTerm = AUTO_MODERATION_TERMS.some((term) => normalized.includes(term));
  if (hasBlockedTerm) {
    reasons.push("linguagem_inadequada");
  }

  const linksCount = (text.match(/https?:\/\//gi) || []).length;
  if (linksCount >= 2) {
    reasons.push("spam_links");
  }

  if (/([A-Za-z])\1{8,}/.test(text)) {
    reasons.push("texto_repetitivo");
  }

  if (text.length > 2500) {
    reasons.push("texto_extenso");
  }

  if (hasBlockedTerm) {
    return { status: "hidden", reasons };
  }

  if (reasons.length > 0) {
    return { status: "review", reasons };
  }

  return { status: "approved", reasons: [] };
}

function hasTextValue(value) {
  return typeof value === "string" && value.trim().length > 0;
}

// Todos os campos de comentário que uma avaliação pode conter. Usado para
// contabilizar o total de comentários (um usuário pode comentar em vários
// critérios), evitando subestimar o conteúdo existente na empresa.
const ALL_COMMENT_KEYS = [
  "commentRating",
  "commentSalario",
  "commentBeneficios",
  "commentCultura",
  "commentOportunidades",
  "commentInovacao",
  "commentLideranca",
  "commentDiversidade",
  "commentAmbiente",
  "commentDiscriminacao",
  "commentCargaHoraria",
  "commentCrescimento",
  "commentEquilibrio",
  "commentReconhecimento",
  "commentComunicacao",
  "commentEtica",
  "commentDesenvolvimento",
  "commentSaudeBemEstar",
  "commentImpactoSocial",
  "commentReputacao",
  "commentEstimacaoOrganizacao",
  "generalComment",
  "commentGeral",
];

async function fetchWikidataLabels(ids) {
  if (!ids.length) return {};
  const url = `https://www.wikidata.org/w/api.php?action=wbgetentities&ids=${ids.join("|")}&format=json&props=labels&languages=pt|en&origin=*`;
  const response = await fetch(url);
  if (!response.ok) return {};
  const data = await response.json();
  const entities = data?.entities || {};

  const labels = {};
  Object.keys(entities).forEach((id) => {
    labels[id] =
      entities[id]?.labels?.pt?.value ||
      entities[id]?.labels?.en?.value ||
      id;
  });

  return labels;
}

async function fetchCompanyInsightsByName(companyName) {
  if (!companyName) return null;

  const searchUrl = `https://www.wikidata.org/w/api.php?action=wbsearchentities&search=${encodeURIComponent(
    companyName
  )}&language=pt&uselang=pt&type=item&limit=5&format=json&origin=*`;

  const searchRes = await fetch(searchUrl);
  if (!searchRes.ok) return null;
  const searchData = await searchRes.json();
  const items = searchData?.search || [];
  if (!items.length) return null;

  const normalizedCompanyName = normalizeKey(companyName);
  const bestMatch =
    items.find((item) => normalizeKey(item.label).includes(normalizedCompanyName)) ||
    items[0];

  const entityId = bestMatch?.id;
  if (!entityId) return null;

  const detailsUrl = `https://www.wikidata.org/w/api.php?action=wbgetentities&ids=${entityId}&format=json&props=labels|descriptions|claims&languages=pt|en&origin=*`;
  const detailsRes = await fetch(detailsUrl);
  if (!detailsRes.ok) return null;
  const detailsData = await detailsRes.json();
  const entity = detailsData?.entities?.[entityId];
  if (!entity) return null;

  const getClaimValue = (claimKey) => {
    const claim = entity?.claims?.[claimKey]?.[0];
    return claim?.mainsnak?.datavalue?.value;
  };

  const industryValue = getClaimValue("P452");
  const hqValue = getClaimValue("P159");
  const websiteValue = getClaimValue("P856");
  const linkedinValue = getClaimValue("P6634");
  const twitterValue = getClaimValue("P2002");
  const instagramValue = getClaimValue("P2003");
  const facebookValue = getClaimValue("P2013");

  const idCandidates = [industryValue?.id, hqValue?.id].filter(Boolean);
  const labelsMap = await fetchWikidataLabels(idCandidates);

  return {
    description:
      entity?.descriptions?.pt?.value ||
      entity?.descriptions?.en?.value ||
      bestMatch?.description ||
      "Não identificado automaticamente",
    sector: industryValue?.id ? labelsMap[industryValue.id] : "Não identificado automaticamente",
    location: hqValue?.id ? labelsMap[hqValue.id] : "Não identificado automaticamente",
    website: typeof websiteValue === "string" ? websiteValue : "Não identificado automaticamente",
    socialLinks: [
      { label: "LinkedIn", value: linkedinValue ? `https://www.linkedin.com/company/${linkedinValue}` : "Não identificado automaticamente" },
      { label: "X / Twitter", value: twitterValue ? `https://twitter.com/${twitterValue}` : "Não identificado automaticamente" },
      { label: "Instagram", value: instagramValue ? `https://instagram.com/${instagramValue}` : "Não identificado automaticamente" },
      { label: "Facebook", value: facebookValue ? `https://facebook.com/${facebookValue}` : "Não identificado automaticamente" },
    ],
  };
}

function CompanyDetails({ theme, toggleTheme }) {
  const EDIT_DELETE_WINDOW_MS = 5 * 60 * 1000;
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const name = searchParams.get("name");
  const slugParam = searchParams.get("slug");
  const [company, setCompany] = useState(null);

  // Aba ativa do perfil da empresa: "overview" (Visão Geral) ou
  // "selectionProcess" (Processo Seletivo).
  const [activeTab, setActiveTab] = useState("overview");

  /* ── Apoiadores recomendados ── */
  const [apoiadoresRecomendados, setApoiadoresRecomendados] = useState([]);
  const [advogadosTrabalhistas, setAdvogadosTrabalhistas] = useState([]);
  useEffect(() => {
    (async () => {
      try {
        const snap = await getDocs(
          query(collection(db, "apoiadores"), where("status", "==", "ativo"), where("plano", "==", "premium"), orderBy("rating", "desc"), limit(3))
        );
        const list = filterOutTestApoiadores(
          snap.docs.map((d) => ({ id: d.id, ...d.data() }))
        );
        setApoiadoresRecomendados(list);
      } catch (err) {
        if (err?.code === "failed-precondition") {
          console.error(
            "%c[Firestore] Índice composto necessário para apoiadores (status + plano + rating). Crie no Firebase Console:",
            "color:#b45309;font-weight:bold",
            err?.message || err
          );
        }
      }
    })();
    (async () => {
      try {
        const snap = await getDocs(
          query(collection(db, "apoiadores"), where("status", "==", "ativo"), where("tipo", "==", "advogado"), orderBy("rating", "desc"), limit(6))
        );
        setAdvogadosTrabalhistas(
          filterOutTestApoiadores(
            snap.docs.map((d) => ({ id: d.id, ...d.data() }))
          )
        );
      } catch (err) {
        if (err?.code === "failed-precondition") {
          console.error(
            "%c[Firestore] Índice composto necessário para apoiadores (status + tipo + rating). Crie no Firebase Console:",
            "color:#b45309;font-weight:bold",
            err?.message || err
          );
        }
      }
    })();
  }, []);




  // Busca empresa ao montar/com nome mudar
  useEffect(() => {
    let cancelled = false;

    const resolveLocal = () => {
      try {
        const stored = localStorage.getItem("empresasData");
        if (!stored) return null;
        const empresas = JSON.parse(stored);
        if (!Array.isArray(empresas)) return null;

        if (name) {
          const byName = empresas.find((emp) => emp.company === name);
          if (byName) return byName;
        }
        if (slugParam) {
          const target = String(slugParam).toLowerCase();
          const bySlug = empresas.find((emp) => {
            const empSlug = (emp.slug || toSlug(emp.company || "")).toLowerCase();
            return empSlug === target;
          });
          if (bySlug) return bySlug;
        }
      } catch {
        // ignore
      }
      return null;
    };

    const resolveRemote = async () => {
      if (!slugParam) return null;
      try {
        const ref = doc(db, "companies", String(slugParam).toLowerCase());
        const snap = await getDoc(ref);
        if (!snap.exists()) return null;
        const data = snap.data() || {};
        return {
          ...data,
          company: data.name || data.company || data.razaoSocial || slugParam,
          slug: data.slug || String(slugParam).toLowerCase(),
        };
      } catch {
        return null;
      }
    };

    if (!name && !slugParam) {
      setCompany(null);
      return undefined;
    }

    const local = resolveLocal();
    if (local) {
      setCompany(local);
      return undefined;
    }

    setCompany(null);
    (async () => {
      const remote = await resolveRemote();
      if (!cancelled && remote) setCompany(remote);
    })();

    return () => {
      cancelled = true;
    };
  }, [name, slugParam]);

  // Enriquecimento automático via Brasil API
  useEffect(() => {
    async function tryEnrichCompany() {
      if (!company?.cnpj || !company?.slug) return;
      if (!company.ramo || !company.cidade || !company.estado || !company.descricao) {
        const enriched = await enrichCompanyWithBrasilAPI(company.cnpj);
        if (enriched) {
          const ref = doc(db, "companies", company.slug);
          await updateDoc(ref, enriched);
        }
      }
    }
    tryEnrichCompany();
  }, [company]);

  const calculateAverage = (emp) => {
    if (!emp) return "--";
    const values = [
      emp.comunicacao,
      emp.etica,
      emp.salario,
      emp.cultura,
      emp.saudeBemEstar,
      emp.lideranca,
      emp.ambiente,
      emp.estimacaoOrganizacao,
      emp.desenvolvimento,
      emp.reconhecimento,
      emp.equilibrio,
      emp.diversidade,
      emp.cargaHoraria,
      emp.crescimento,
      emp.discriminacao,
      emp.rating,
    ].filter((v) => typeof v === "number" && !isNaN(v) && v > 0);
    if (values.length === 0) return "--";
    const sum = values.reduce((acc, curr) => acc + curr, 0);
    const avg = sum / values.length;
    return Number.isFinite(avg) ? avg.toFixed(1) : "--";
  };

  const openLinkedInJobs = () => {
    if (!company?.company) return;
    const url = `https://www.linkedin.com/jobs/search/?keywords=${encodeURIComponent(company.company)}`;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const getEvaluations = () => {
    if (!company) return {};
    try {
      const stored = localStorage.getItem(`evaluations_${company.company}`);
      return stored ? JSON.parse(stored) : {};
    } catch (err) {
      return {};
    }
  };

  const getScoreColor = (score) => {
    const n = parseFloat(score);
    if (Number.isNaN(n)) return "text-gray-700";
    if (n < 2) return "text-red-600";
    if (n < 3) return "text-purple-600";
    if (n < 4) return "text-yellow-600";
    if (n < 4.5) return "text-lime-600";
    return "text-emerald-700";
  };

  const [comments, setComments] = React.useState([]);
  const [mandatoryComment, setMandatoryComment] = React.useState("");
  const [commentError, setCommentError] = React.useState("");
  const [replyTo, setReplyTo] = React.useState(null);
  const [replyText, setReplyText] = React.useState("");
  const [reactionRegistry, setReactionRegistry] = React.useState({});
  const [animatedReactionKey, setAnimatedReactionKey] = React.useState("");
  const [blockedAuthors, setBlockedAuthors] = React.useState({});
  const [hiddenContentIds, setHiddenContentIds] = React.useState({});
  const [reportsRegistry, setReportsRegistry] = React.useState({});
  const [moderationInfo, setModerationInfo] = React.useState("");
  const [actionNotice, setActionNotice] = React.useState("");
  const [editingTargetId, setEditingTargetId] = React.useState(null);
  const [editingText, setEditingText] = React.useState("");
  const [openReactionPickerId, setOpenReactionPickerId] = React.useState(null);
  const [nowTimestamp, setNowTimestamp] = React.useState(Date.now());
  const reactionAnimationTimeout = React.useRef(null);
  const reactionHoldTimeout = React.useRef(null);
  const [insights, setInsights] = React.useState(null);
  const [insightsLoading, setInsightsLoading] = React.useState(false);

    // ─── Resposta oficial da empresa ───
    const [companyReplyTargetId, setCompanyReplyTargetId] = React.useState(null);
    const [companyReplyText, setCompanyReplyText] = React.useState("");
    const [companyReplySaving, setCompanyReplySaving] = React.useState(false);

    // RBAC
    const userRole = React.useMemo(() => getUserRole(), []);
    const userIsPremium = React.useMemo(() => isPremium(), []);
    const userIsLoggedIn = React.useMemo(() => {
      try {
        const p = JSON.parse(localStorage.getItem("userProfile") || "{}");
        const provider = (p?.loginProvider || "").toString().toLowerCase();
        return provider && provider !== "anonymous" && !p?.fallback;
      } catch { return false; }
    }, []);
    const isEmpresaPremium = userIsPremium && (userRole === "admin_empresa" || userRole === "empresa");

    // Selo de Perfil Verificado: requisito para publicar avaliações.
    // Usuários sem o selo continuam podendo visualizar empresas e cadastrar
    // novas, mas a publicação de comentários/notas é bloqueada.
    const userIsCertified = React.useMemo(() => {
      try {
        const p = JSON.parse(localStorage.getItem("userProfile") || "{}");
        return isUserProfileCertified(p) || isAdmin();
      } catch { return false; }
    }, []);

    // Dashboard premium: dados de tendência por período
    const [trendData, setTrendData] = React.useState([]);
    const [trendLoading, setTrendLoading] = React.useState(false);
    const [trendError, setTrendError] = React.useState("");
    const [periodStart, setPeriodStart] = React.useState("");
    const [periodEnd, setPeriodEnd] = React.useState("");
    const [selectedTrendKey, setSelectedTrendKey] = React.useState("rating");
    const [dashboardVisible, setDashboardVisible] = React.useState(false);
    const [checkoutLoadingAudience, setCheckoutLoadingAudience] = React.useState(null);
    // Removidos: premiumNotice, setPremiumNotice, premiumAudience, setPremiumAudience pois não são mais usados
  // Removido premiumPaymentMethod e setPremiumPaymentMethod pois não são mais usados
  const [compareOptions, setCompareOptions] = React.useState([]);
  const [compareTargetSlug, setCompareTargetSlug] = React.useState("");

  // Removidos: compareLoading, setCompareLoading, compareError, setCompareError pois não são mais usados
  const logoCandidates = company
    ? getCompanyLogoCandidates(company.company, { size: 128, website: company.website })
    : [];
  const [logoIndex, setLogoIndex] = React.useState(0);
  const companyLogo = logoCandidates[logoIndex] || null;
  const companyInitials = React.useMemo(() => {
    const name = (company?.company || "").trim();
    if (!name) return "?";
    const parts = name.split(/\s+/).filter(Boolean);
    const letters = parts.length === 1 ? parts[0].slice(0, 2) : parts[0][0] + parts[parts.length - 1][0];
    return letters.toUpperCase();
  }, [company]);
  const [itemCommentCounts, setItemCommentCounts] = React.useState({});
  const [companyReviewCount, setCompanyReviewCount] = React.useState(0);
  const [companyCommentTotal, setCompanyCommentTotal] = React.useState(0);
  const [companyAverages, setCompanyAverages] = React.useState({});


  const average = companyReviewCount > 0
    ? calculateAverage({ ...company, ...companyAverages })
    : calculateAverage(company);
  const averageScore = Number.parseFloat(average);
  const hasAverageScore = Number.isFinite(averageScore);
  const isRecommendedCompany = hasAverageScore && averageScore >= 3;

  const evaluations = getEvaluations();
  const localEvaluationCount = Object.keys(evaluations).length;
  const evaluationCount = companyReviewCount || localEvaluationCount;

  React.useEffect(() => {
    setLogoIndex(0);
  }, [company?.company, company?.website]);



  React.useEffect(() => {
    return () => {
      if (reactionAnimationTimeout.current) {
        clearTimeout(reactionAnimationTimeout.current);
      }
      if (reactionHoldTimeout.current) {
        clearTimeout(reactionHoldTimeout.current);
      }
    };
  }, []);

  React.useEffect(() => {
    const timer = setInterval(() => {
      setNowTimestamp(Date.now());
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  React.useEffect(() => {
    let alive = true;

    const loadInsights = async () => {
      if (!company?.company) {
        setInsights(null);
        return;
      }

      setInsightsLoading(true);
      try {
        const remote = await fetchCompanyInsightsByName(company.company);
        if (!alive) return;
        setInsights(remote);
      } catch (err) {
        if (!alive) return;
        setInsights(null);
      } finally {
        if (!alive) return;
        setInsightsLoading(false);
      }
    };

    loadInsights();

    return () => {
      alive = false;
    };
  }, [company?.company]);

  React.useEffect(() => {
    let alive = true;

    const loadItemCommentsCount = async () => {
      const companySlug = (company?.company || "")
        .toString()
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .toLowerCase()
        .trim()
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-+/g, "")
        .replace(/-+$/g, "");
      if (!companySlug) {
        setItemCommentCounts({});
        setCompanyCommentTotal(0);
        return;
      }

      try {
        const reviews = await listReviewsByCompanySlug(companySlug, 300);
        if (!alive) return;

        const nextCounts = {
          comunicacao: 0,
          etica: 0,
          salario: 0,
          cultura: 0,
          saudeBemEstar: 0,
          lideranca: 0,
          ambiente: 0,
          estimacaoOrganizacao: 0,
          desenvolvimento: 0,
          reconhecimento: 0,
          equilibrio: 0,
          diversidade: 0,
          cargaHoraria: 0,
          crescimento: 0,
          discriminacao: 0,
          rating: 0,
        };

        const metricKeys = [
          "comunicacao",
          "etica",
          "salario",
          "cultura",
          "saudeBemEstar",
          "lideranca",
          "ambiente",
          "estimacaoOrganizacao",
          "desenvolvimento",
          "reconhecimento",
          "equilibrio",
          "diversidade",
          "cargaHoraria",
          "crescimento",
          "discriminacao",
          "rating",
        ];
        const totals = Object.fromEntries(metricKeys.map((key) => [key, 0]));
        const nextSourceStats = {
          indicacao: 0,
          siteVagas: 0,
          gruposWhatsapp: 0,
          redesSociais: 0,
        };

        let commentTotal = 0;
        for (const review of reviews || []) {
          if (hasTextValue(review?.commentComunicacao)) nextCounts.comunicacao += 1;
          if (hasTextValue(review?.commentEtica)) nextCounts.etica += 1;
          if (hasTextValue(review?.commentSalario) || hasTextValue(review?.commentBeneficios)) nextCounts.salario += 1;
          if (hasTextValue(review?.commentCultura)) nextCounts.cultura += 1;
          if (hasTextValue(review?.commentSaudeBemEstar)) nextCounts.saudeBemEstar += 1;
          if (hasTextValue(review?.commentLideranca)) nextCounts.lideranca += 1;
          if (hasTextValue(review?.commentAmbiente)) nextCounts.ambiente += 1;
          if (hasTextValue(review?.commentEstimacaoOrganizacao)) nextCounts.estimacaoOrganizacao += 1;
          if (hasTextValue(review?.commentDesenvolvimento)) nextCounts.desenvolvimento += 1;
          if (hasTextValue(review?.commentReconhecimento)) nextCounts.reconhecimento += 1;
          if (hasTextValue(review?.commentEquilibrio)) nextCounts.equilibrio += 1;
          if (hasTextValue(review?.commentDiversidade)) nextCounts.diversidade += 1;
          if (hasTextValue(review?.commentCargaHoraria)) nextCounts.cargaHoraria += 1;
          if (hasTextValue(review?.commentCrescimento)) nextCounts.crescimento += 1;
          if (hasTextValue(review?.commentDiscriminacao)) nextCounts.discriminacao += 1;
          if (hasTextValue(review?.commentRating)) nextCounts.rating += 1;

          // Total de comentários preenchidos por esta avaliação (todos os critérios).
          for (const commentKey of ALL_COMMENT_KEYS) {
            if (hasTextValue(review?.[commentKey])) commentTotal += 1;
          }

          for (const metricKey of metricKeys) {
            totals[metricKey] += Number(review?.[metricKey]) || 0;
          }

          if (review?.entrySource && nextSourceStats[review.entrySource] != null) {
            nextSourceStats[review.entrySource] += 1;
          }
        }

        setItemCommentCounts(nextCounts);
        setCompanyCommentTotal(commentTotal);
        const reviewCount = (reviews || []).length;
        setCompanyReviewCount(reviewCount);
        setCompanyAverages(
          Object.fromEntries(
            metricKeys.map((key) => [
              key,
              reviewCount > 0 ? Number((totals[key] / reviewCount).toFixed(2)) : 0,
            ])
          )
        );
      } catch (err) {
        if (!alive) return;
        setItemCommentCounts({});
        setCompanyReviewCount(0);
        setCompanyCommentTotal(0);
        setCompanyAverages({});
      }
    };

    loadItemCommentsCount();

    return () => {
      alive = false;
    };
  }, [company?.company]);

  const reactions = [
    { key: "thumbsUp", emoji: "👍", label: "Útil" },
    { key: "thumbsDown", emoji: "👎", label: "Não Útil" },
    { key: "clap", emoji: "👏", label: "Concordo" },
    { key: "surprised", emoji: "😮", label: "Surpreso" },
    { key: "cry", emoji: "😢", label: "Triste" },
  ];

  const clearReactionHoldTimer = () => {
    if (!reactionHoldTimeout.current) return;
    clearTimeout(reactionHoldTimeout.current);
    reactionHoldTimeout.current = null;
  };

  const startReactionHold = (pickerId) => {
    clearReactionHoldTimer();
    reactionHoldTimeout.current = setTimeout(() => {
      setOpenReactionPickerId(pickerId);
      reactionHoldTimeout.current = null;
    }, 450);
  };

  const getTotalReactions = (comment) => {
    return Object.values(comment.reactions || {}).reduce((sum, v) => sum + (v || 0), 0);
  };

  const getCurrentPseudonym = () => {
    return (localStorage.getItem("userPseudonym") || "Anônimo").toString().trim();
  };

  const getContentCreatedAtMs = (item) => {
    const ts = new Date(item?.createdAt || "").getTime();
    return Number.isFinite(ts) ? ts : null;
  };

  const isOwnedByCurrentUser = (item) => {
    return normalizeKey(item?.author) === normalizeKey(getCurrentPseudonym());
  };

  const canManageContent = (item) => {
    if (!item?.id || !isOwnedByCurrentUser(item)) return false;
    const createdAtMs = getContentCreatedAtMs(item);
    if (createdAtMs == null) return false;
    return nowTimestamp - createdAtMs <= EDIT_DELETE_WINDOW_MS;
  };

  const getRemainingManageTimeLabel = (item) => {
    const createdAtMs = getContentCreatedAtMs(item);
    if (createdAtMs == null) return "";

    const remainingMs = Math.max(0, EDIT_DELETE_WINDOW_MS - (nowTimestamp - createdAtMs));
    if (remainingMs <= 0) return "";

    const remainingSeconds = Math.ceil(remainingMs / 1000);
    const minutes = Math.floor(remainingSeconds / 60);
    const seconds = remainingSeconds % 60;
    return `${minutes}m ${String(seconds).padStart(2, "0")}s`;
  };

  const getPositiveReactions = (comment) => {
    return (
      (comment?.reactions?.thumbsUp || 0) +
      (comment?.reactions?.clap || 0) +
      (comment?.reactions?.surprised || comment?.reactions?.laugh || 0)
    );
  };

  const companyInfo = useMemo(() => {
    if (!company) return null;

    const sector =
      insights?.sector ||
      company.ramo ||
      company.setor ||
      company.segmento ||
      company.industry ||
      "Não identificado automaticamente";

    const location =
      insights?.location ||
      [company.cidade, company.estado, company.pais].filter(Boolean).join(" - ") ||
      "Não identificado automaticamente";

    const website =
      insights?.website ||
      company.website ||
      company.site ||
      company.url ||
      "Não identificado automaticamente";

    const cnpj = company.cnpj || "Não informado";

    const cnpjDigits = String(company.cnpj || "").replace(/\D/g, "");
    const cnpjFormatted = cnpjDigits.length === 14
      ? cnpjDigits.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/, "$1.$2.$3.$4-$5")
      : (company.cnpj || "Não informado");

    const razaoSocial =
      company.razaoSocial || company.razao_social || "Não informado";

    const nomeFantasia =
      company.nomeFantasia || company.nome_fantasia || company.fantasia || "Não informado";

    const endereco =
      company.endereco ||
      [
        company.logradouro,
        company.numero ? `, ${company.numero}` : "",
        company.bairro ? ` - ${company.bairro}` : "",
        company.cidade ? ` - ${company.cidade}` : "",
        company.estado ? `/${company.estado}` : "",
      ].join("").trim() ||
      "Não informado";

    const socialLinks = insights?.socialLinks || [
      { label: "LinkedIn", value: company.linkedin || "Não identificado automaticamente" },
      { label: "Instagram", value: company.instagram || "Não identificado automaticamente" },
      { label: "Facebook", value: company.facebook || "Não identificado automaticamente" },
    ];

    const description = insights?.description || "Informação automática ainda não identificada para esta empresa.";

    return {
      sector,
      location,
      website,
      cnpj,
      cnpjFormatted,
      razaoSocial,
      nomeFantasia,
      endereco,
      socialLinks,
      description,
    };
  }, [company, insights]);

  const userHasResumeProof = useMemo(() => {
    if (!company?.company) return false;
    try {
      const userProfile = JSON.parse(localStorage.getItem("userProfile") || "{}");
      return hasCompanyInResumeExperiences(company.company, userProfile?.resumeData);
    } catch {
      return false;
    }
  }, [company?.company]);

  const fetchTrend = React.useCallback(async () => {
    if (!userIsPremium || !company?.company) return;
    const slug = company.company
      .toString()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-");
    setTrendLoading(true);
    setTrendError("");
    try {
      const profile = JSON.parse(localStorage.getItem("userProfile") || "{}");
      const res = await fetch("/api/admin?op=reviews", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          companySlug: slug,
          is_premium: profile?.is_premium || userRole === "admin_empresa",
          periodStart: periodStart || undefined,
          periodEnd: periodEnd || undefined,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Erro ao buscar tendências.");
      setTrendData(data.trend || []);
    } catch (err) {
      setTrendError(err.message);
    } finally {
      setTrendLoading(false);
    }
  }, [userIsPremium, company?.company, userRole, periodStart, periodEnd]);

  React.useEffect(() => {
    if (userIsPremium && dashboardVisible) fetchTrend();
  }, [fetchTrend, userIsPremium, dashboardVisible]);

  const handlePremiumUnlock = React.useCallback(async (audience = "worker") => {
    // Linha removida: setPremiumNotice não é mais usado
    const companyName = (company?.company || "").toString().trim();
    const companySlug = companyName
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+/g, "")
      .replace(/-+$/g, "");
    const cnpj = (companyInfo?.cnpj || "").toString();
    const cleaned = cnpj.replace(/\D/g, "");
    if (!companySlug) {
      // Linha removida: setPremiumNotice não é mais usado
      return;
    }

    setCheckoutLoadingAudience(audience);
    try {
      await handleCheckout({
        cnpj: cleaned,
        companySlug,
        companyName,
        audience,
      });
    } catch (err) {
      // Linha removida: setPremiumNotice não é mais usado
    } finally {
      setCheckoutLoadingAudience(null);
    }
  }, [company?.company, companyInfo?.cnpj]);

  const getCommentsKey = React.useCallback(() => {
    return company ? `comments_${company.company}` : null;
  }, [company]);

  const getCompanySlug = React.useCallback(() => {
    if (!company?.company) return "";
    return toSlug(company.company);
  }, [company]);

  React.useEffect(() => {
    let alive = true;

    const loadCompareOptions = async () => {
      const currentSlug = getCompanySlug();
      const currentSegment = (company?.segmento || "").toString().trim();
      if (!currentSlug) {
        if (alive) {
          setCompareOptions([]);
          setCompareTargetSlug("");
        }
        return;
      }

      const optionMap = new Map();

      try {
        const stored = JSON.parse(localStorage.getItem("empresasData") || "[]");
        for (const item of stored) {
          const itemName = (item?.company || item?.name || "").toString().trim();
          const itemSlug = toSlug(itemName);
          const itemSegment = (item?.segmento || "").toString().trim();
          if (!itemSlug || itemSlug === currentSlug) continue;
          if (currentSegment && itemSegment !== currentSegment) continue;
          if (!optionMap.has(itemSlug)) {
            optionMap.set(itemSlug, { slug: itemSlug, name: itemName || itemSlug });
          }
        }
      } catch {
        // fallback silencioso
      }

      try {
        const remoteCompanies = await listCompanies(250);
        for (const item of remoteCompanies || []) {
          const itemName = (item?.name || item?.company || item?.slug || "").toString().trim();
          const itemSlug = toSlug(item?.slug || itemName);
          const itemSegment = (item?.segmento || "").toString().trim();
          if (!itemSlug || itemSlug === currentSlug) continue;
          if (currentSegment && itemSegment !== currentSegment) continue;
          if (!optionMap.has(itemSlug)) {
            optionMap.set(itemSlug, { slug: itemSlug, name: itemName || itemSlug });
          }
        }
      } catch {
        // mantém opções locais se o remoto falhar
      }

      if (!alive) return;

      const merged = [...optionMap.values()]
        .sort((a, b) => a.name.localeCompare(b.name, "pt-BR"))
        .slice(0, 80);

      setCompareOptions(merged);
      if (!merged.some((item) => item.slug === compareTargetSlug)) {
        setCompareTargetSlug(merged[0]?.slug || "");
      }
    };

    loadCompareOptions();

    return () => {
      alive = false;
    };
  }, [company?.company, company?.segmento, getCompanySlug, compareTargetSlug]);

  const getReactionsKey = React.useCallback(() => {
    return company ? `comment_reactions_${company.company}` : null;
  }, [company]);

  const getBlockedAuthorsKey = React.useCallback(() => {
    return company ? `blocked_authors_${company.company}` : null;
  }, [company]);

  const getHiddenContentKey = React.useCallback(() => {
    return company ? `hidden_content_${company.company}` : null;
  }, [company]);

  const getReportsRegistryKey = React.useCallback(() => {
    return company ? `reports_registry_${company.company}` : null;
  }, [company]);

  const saveComments = React.useCallback((nextComments) => {
    setComments(nextComments);
    try {
      const key = getCommentsKey();
      if (key) {
        localStorage.setItem(key, JSON.stringify(nextComments));
      }
    } catch (err) {
      console.warn("Falha ao salvar comentários:", err);
    }
  }, [getCommentsKey]);

  const syncCommentsToFirestore = async (nextComments) => {
    try {
      const companySlug = getCompanySlug();
      if (!companySlug) return;

      await Promise.all(
        (nextComments || []).map((comment) =>
          setDoc(
            doc(db, "comments", comment.id),
            {
              ...comment,
              companySlug,
              companyName: company.company,
              updatedAt: new Date().toISOString(),
            },
            { merge: true }
          )
        )
      );
    } catch (err) {
      console.warn("Falha ao sincronizar comentários no Firebase:", err);
    }
  };

  React.useEffect(() => {
    const key = getCommentsKey();
    if (!key) {
      setComments([]);
      return;
    }

    try {
      const stored = localStorage.getItem(key);
      if (stored) {
        setComments(JSON.parse(stored));
      } else {
        setComments([]);
      }
    } catch (err) {
      console.warn("Falha ao carregar comentários:", err);
      setComments([]);
    }
  }, [getCommentsKey]);

  React.useEffect(() => {
    const fetchRemoteComments = async () => {
      try {
        const companySlug = getCompanySlug();
        if (!companySlug) return;

        const ref = collection(db, "comments");
        const q = query(
          ref,
          where("companySlug", "==", companySlug),
          orderBy("createdAt", "desc"),
          limit(120)
        );

        const snap = await getDocs(q);
        if (snap.empty) return;

        const normalized = snap.docs.map((d) => {
          const data = d.data();
          return {
            id: d.id,
            author: data.author || "Anônimo",
            authorProfileId: data.authorProfileId || null,
            text: data.text || "",
            createdAt:
              typeof data.createdAt?.toDate === "function"
                ? data.createdAt.toDate().toISOString()
                : data.createdAt || new Date().toISOString(),
            editedAt:
              typeof data.editedAt?.toDate === "function"
                ? data.editedAt.toDate().toISOString()
                : data.editedAt || null,
            reactions: {
              thumbsUp: data.reactions?.thumbsUp || 0,
              thumbsDown: data.reactions?.thumbsDown || 0,
              clap: data.reactions?.clap || 0,
              // Compatibilidade com dados antigos que usavam "laugh".
              surprised: data.reactions?.surprised || data.reactions?.laugh || 0,
              cry: data.reactions?.cry || 0,
            },
            replies: Array.isArray(data.replies)
              ? data.replies.map((reply) => ({
                  ...reply,
                  editedAt:
                    typeof reply?.editedAt?.toDate === "function"
                      ? reply.editedAt.toDate().toISOString()
                      : reply?.editedAt || null,
                }))
              : [],
            companyReply: data.companyReply || null,
          };
        });

        saveComments(normalized);
      } catch (err) {
        if (err?.code === "failed-precondition") {
          console.error(
            "%c[Firestore] Índice composto necessário para comments (companySlug + createdAt desc). Crie no Firebase Console:",
            "color:#b45309;font-weight:bold",
            err?.message || err
          );
        } else {
          console.warn("Falha ao carregar comentários do Firebase:", err);
        }
      }
    };

    fetchRemoteComments();
  }, [getCompanySlug, saveComments]);

  React.useEffect(() => {
    const key = getReactionsKey();
    if (!key) {
      setReactionRegistry({});
      return;
    }

    let cancelled = false;
    let initial = {};
    try {
      const stored = localStorage.getItem(key);
      initial = stored ? JSON.parse(stored) : {};
    } catch (err) {
      console.warn("Falha ao carregar reações:", err);
      initial = {};
    }
    setReactionRegistry(initial);

    // Sincroniza reações do usuário a partir do Firestore para impor "1 reação por usuário".
    (async () => {
      try {
        const userId = getStableUserId();
        const companySlug = getCompanySlug();
        if (!userId || !companySlug) return;
        const q = query(
          collection(db, "commentReactions"),
          where("userId", "==", userId),
          where("companySlug", "==", companySlug)
        );
        const snap = await getDocs(q);
        if (cancelled) return;
        const merged = { ...initial };
        snap.forEach((d) => {
          const data = d.data() || {};
          if (data.commentId && data.reaction) {
            merged[`${data.commentId}__${userId}`] = data.reaction;
          }
        });
        setReactionRegistry(merged);
        try { localStorage.setItem(key, JSON.stringify(merged)); } catch {}
      } catch (err) {
        console.warn("Falha ao carregar reações do Firestore:", err);
      }
    })();

    return () => { cancelled = true; };
    // getStableUserId é definido mais abaixo no componente (TDZ); incluí-lo
    // no array quebraria a inicialização. É estável (useCallback) e seguro omitir.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getReactionsKey, getCompanySlug]);

  React.useEffect(() => {
    const key = getBlockedAuthorsKey();
    if (!key) {
      setBlockedAuthors({});
      return;
    }

    try {
      const stored = localStorage.getItem(key);
      setBlockedAuthors(stored ? JSON.parse(stored) : {});
    } catch {
      setBlockedAuthors({});
    }
  }, [getBlockedAuthorsKey]);

  React.useEffect(() => {
    const key = getHiddenContentKey();
    if (!key) {
      setHiddenContentIds({});
      return;
    }

    try {
      const stored = localStorage.getItem(key);
      setHiddenContentIds(stored ? JSON.parse(stored) : {});
    } catch {
      setHiddenContentIds({});
    }
  }, [getHiddenContentKey]);

  React.useEffect(() => {
    const key = getReportsRegistryKey();
    if (!key) {
      setReportsRegistry({});
      return;
    }

    try {
      const stored = localStorage.getItem(key);
      setReportsRegistry(stored ? JSON.parse(stored) : {});
    } catch {
      setReportsRegistry({});
    }
  }, [getReportsRegistryKey]);

  const saveReactionRegistry = (next) => {
    setReactionRegistry(next);
    try {
      const key = getReactionsKey();
      if (key) {
        localStorage.setItem(key, JSON.stringify(next));
      }
    } catch (err) {
      console.warn("Falha ao salvar reações:", err);
    }
  };

  const saveHiddenContentIds = (next) => {
    setHiddenContentIds(next);
    try {
      const key = getHiddenContentKey();
      if (key) localStorage.setItem(key, JSON.stringify(next));
    } catch {
      // ignore
    }
  };

  const saveReportsRegistry = (next) => {
    setReportsRegistry(next);
    try {
      const key = getReportsRegistryKey();
      if (key) localStorage.setItem(key, JSON.stringify(next));
    } catch {
      // ignore
    }
  };

  const getAuthorBlockKey = (author) => normalizeKey(author || "anonimo");

  const isAuthorBlocked = React.useCallback(
    (author) => Boolean(blockedAuthors[getAuthorBlockKey(author)]),
    [blockedAuthors]
  );

  const isContentHidden = React.useCallback(
    (targetId) => Boolean(hiddenContentIds[targetId]),
    [hiddenContentIds]
  );

  const hideContent = (targetId) => {
    if (!targetId) return;
    saveHiddenContentIds({
      ...hiddenContentIds,
      [targetId]: {
        hiddenAt: new Date().toISOString(),
      },
    });
    setModerationInfo("Conteúdo ocultado com sucesso.");
  };

  const syncReportToFirestore = async (report) => {
    try {
      await setDoc(doc(db, "content_reports", report.id), report, { merge: true });
    } catch (err) {
      console.warn("Falha ao salvar denuncia no Firebase:", err);
    }
  };

  const reportTarget = ({ targetId, targetType, author, text }) => {
    const reporter = localStorage.getItem("userPseudonym") || "anon";
    const registryKey = `${targetId}__${reporter}`;
    if (reportsRegistry[registryKey]) {
      setModerationInfo("Você já denunciou este item.");
      return;
    }

    const moderationSnapshot = detectAutoModeration(text || "");
    const report = {
      id: `report_${Date.now()}_${Math.random().toString(16).slice(2)}`,
      companyName: company?.company || "",
      companySlug: getCompanySlug(),
      targetId,
      targetType,
      targetAuthor: author || "Anônimo",
      reporter,
      textSnippet: (text || "").slice(0, 320),
      autoModerationStatus: moderationSnapshot.status,
      autoModerationReasons: moderationSnapshot.reasons,
      createdAt: new Date().toISOString(),
      status: "pending_review",
    };

    if (moderationSnapshot.status === "hidden") {
      hideContent(targetId);
    }

    syncReportToFirestore(report);
    saveReportsRegistry({
      ...reportsRegistry,
      [registryKey]: report.id,
    });
    setModerationInfo("Denúncia enviada. Moderação automática aplicada.");
  };

  const handleAddComment = () => {
    if (!userIsCertified) {
      setCommentError(
        "Para manter a legitimidade das avaliações, apenas usuários com perfil verificado podem avaliar empresas. Conclua seu perfil e verifique suas experiências para liberar esta funcionalidade."
      );
      return;
    }
    const mandatoryText = mandatoryComment.trim();

    if (!mandatoryText) {
      setCommentError("O comentário precisa ser preenchido.");
      return;
    }

    setCommentError("");

    const pseudonym = localStorage.getItem("userPseudonym") || "Anônimo";
    const storedProfile = (() => {
      try { return JSON.parse(localStorage.getItem("userProfile") || "{}"); } catch { return {}; }
    })();
    const authorProfileId =
      (storedProfile?.profileId || "").toString().trim() ||
      resolveProfileId(storedProfile, { persistGeneratedId: false }) ||
      null;

    const finalText = mandatoryText;

    const moderation = detectAutoModeration(finalText);
    const moderatedText = moderation.status === "approved"
      ? finalText
      : "[Conteúdo ocultado automaticamente pela moderação.]";

    const comment = {
      id: `${Date.now()}_${Math.random().toString(16).slice(2)}`,
      author: pseudonym,
      authorProfileId,
      text: moderatedText,
      createdAt: new Date().toISOString(),
      moderation,
      reactions: { thumbsUp: 0, thumbsDown: 0, clap: 0, surprised: 0, cry: 0 },
      replies: [],
    };
    saveComments([comment, ...comments]);
    syncCommentsToFirestore([comment, ...comments]);
    setActionNotice("Comentário publicado. Você pode editar ou apagar por até 5 minutos.");
    setMandatoryComment("");

    if (moderation.status !== "approved") {
      setModerationInfo("Seu comentário passou por moderação automática.");
    }
  };

  const applyReactionChangeById = (items, targetId, prevKey, nextKey) => {
    return items.map((item) => {
      if (item.id === targetId) {
        const base = {
          thumbsUp: item.reactions?.thumbsUp || 0,
          thumbsDown: item.reactions?.thumbsDown || 0,
          clap: item.reactions?.clap || 0,
          surprised: item.reactions?.surprised || item.reactions?.laugh || 0,
          cry: item.reactions?.cry || 0,
        };
        if (prevKey && base[prevKey] != null) {
          base[prevKey] = Math.max(0, base[prevKey] - 1);
        }
        if (nextKey && base[nextKey] != null) {
          base[nextKey] = base[nextKey] + 1;
        }
        return {
          ...item,
          reactions: base,
        };
      }

      if (item.replies && item.replies.length) {
        return {
          ...item,
          replies: applyReactionChangeById(item.replies, targetId, prevKey, nextKey),
        };
      }

      return item;
    });
  };

  const getStableUserId = React.useCallback(() => {
    try {
      const uid = auth?.currentUser?.uid;
      if (uid) return `uid:${uid}`;
    } catch {}
    try {
      const profile = JSON.parse(localStorage.getItem("userProfile") || "{}");
      if (profile?.id) return `pid:${profile.id}`;
      if (profile?.uid) return `uid:${profile.uid}`;
      if (profile?.email) return `email:${String(profile.email).toLowerCase()}`;
    } catch {}
    const pseudonym = localStorage.getItem("userPseudonym");
    if (pseudonym) return `pseudo:${pseudonym}`;
    return "";
  }, []);

  const getUserReactionFor = (targetId) => {
    const stableUserId = getStableUserId();
    if (!stableUserId) return null;
    const registryKey = `${targetId}__${stableUserId}`;
    return reactionRegistry[registryKey] || null;
  };

  const handleReact = (targetId, reactionKey) => {
    const stableUserId = getStableUserId();
    if (!stableUserId) return;
    const registryKey = `${targetId}__${stableUserId}`;
    const prevReaction = reactionRegistry[registryKey] || null;
    const isSame = prevReaction === reactionKey;
    const nextReaction = isSame ? null : reactionKey;

    const nextComments = applyReactionChangeById(
      comments,
      targetId,
      prevReaction,
      nextReaction
    );
    saveComments(nextComments);
    syncCommentsToFirestore(nextComments);
    setOpenReactionPickerId(null);

    if (nextReaction) {
      const animationKey = `${targetId}__${nextReaction}`;
      setAnimatedReactionKey(animationKey);
      if (reactionAnimationTimeout.current) {
        clearTimeout(reactionAnimationTimeout.current);
      }
      reactionAnimationTimeout.current = setTimeout(() => {
        setAnimatedReactionKey("");
      }, 450);
    } else {
      setAnimatedReactionKey("");
    }

    const nextRegistry = { ...reactionRegistry };
    if (nextReaction) {
      nextRegistry[registryKey] = nextReaction;
    } else {
      delete nextRegistry[registryKey];
    }
    saveReactionRegistry(nextRegistry);

    // Persiste a reação no Firestore para impor unicidade por usuário/comentário.
    (async () => {
      try {
        const userId = getStableUserId();
        const companySlug = getCompanySlug();
        if (!userId || !companySlug) return;
        const safeUserId = userId.replace(/[^a-zA-Z0-9_:.-]/g, "_");
        const docId = `${targetId}__${safeUserId}`;
        const ref = doc(db, "commentReactions", docId);
        if (nextReaction) {
          await setDoc(
            ref,
            {
              commentId: targetId,
              userId,
              companySlug,
              reaction: nextReaction,
              updatedAt: new Date().toISOString(),
            },
            { merge: true }
          );
        } else {
          await deleteDoc(ref);
        }
      } catch (err) {
        console.warn("Falha ao persistir reação no Firestore:", err);
      }
    })();
  };

  const addReplyToItem = (items, targetId, replyObj) => {
    return items.map((item) => {
      if (item.id === targetId) {
        return {
          ...item,
          replies: [...(item.replies || []), replyObj],
        };
      }
      if (item.replies && item.replies.length) {
        return {
          ...item,
          replies: addReplyToItem(item.replies, targetId, replyObj),
        };
      }
      return item;
    });
  };

  const handleReply = (targetId) => {
    if (!replyText.trim()) return;
    const pseudonym = localStorage.getItem("userPseudonym") || "Anônimo";
    const storedProfile = (() => {
      try { return JSON.parse(localStorage.getItem("userProfile") || "{}"); } catch { return {}; }
    })();
    const authorProfileId =
      (storedProfile?.profileId || "").toString().trim() ||
      resolveProfileId(storedProfile, { persistGeneratedId: false }) ||
      null;
    const moderation = detectAutoModeration(replyText.trim());
    const reply = {
      id: `${Date.now()}_${Math.random().toString(16).slice(2)}`,
      author: pseudonym,
      authorProfileId,
      text:
        moderation.status === "approved"
          ? replyText.trim()
          : "[Conteúdo ocultado automaticamente pela moderação.]",
      createdAt: new Date().toISOString(),
      moderation,
      replies: [],
    };
    const next = addReplyToItem(comments, targetId, reply);
    saveComments(next);
    syncCommentsToFirestore(next);
    setActionNotice("Resposta publicada. Você pode editar ou apagar por até 5 minutos.");
    setReplyText("");
    setReplyTo(null);

    if (moderation.status !== "approved") {
      setModerationInfo("Sua resposta passou por moderação automática.");
    }
  };

  // ─── Resposta oficial da empresa a um comentário ───
  const handleCompanyReply = async (commentId) => {
    const text = companyReplyText.trim();
    if (!text || !commentId) return;
    setCompanyReplySaving(true);
    try {
      const companySlug = getCompanySlug();
      if (!companySlug) throw new Error("Empresa não identificada.");

      // Buscar o documento no Firestore para obter a referência correta
      const ref = collection(db, "comments");
      const q = query(ref, where("companySlug", "==", companySlug), limit(200));
      const snap = await getDocs(q);
      const targetDoc = snap.docs.find((d) => d.id === commentId);

      if (!targetDoc) throw new Error("Comentário não encontrado.");

      const replyData = {
        text,
        publishedAt: new Date().toISOString(),
        status: "pending",
      };

      await updateDoc(targetDoc.ref, { companyReply: replyData });

      // Atualizar localmente
      const next = comments.map((c) =>
        c.id === commentId ? { ...c, companyReply: replyData } : c
      );
      saveComments(next);

      setCompanyReplyTargetId(null);
      setCompanyReplyText("");
      setActionNotice("Resposta enviada. Ela ficará visível após aprovação pela moderação.");
    } catch (err) {
      console.warn("Erro ao salvar resposta da empresa:", err);
      setActionNotice("Erro ao enviar resposta. Tente novamente.");
    } finally {
      setCompanyReplySaving(false);
    }
  };

  const updateItemById = (items, targetId, updater) => {
    return items.map((item) => {
      if (item.id === targetId) {
        return updater(item);
      }

      if (item.replies && item.replies.length) {
        return {
          ...item,
          replies: updateItemById(item.replies, targetId, updater),
        };
      }

      return item;
    });
  };

  const removeItemById = (items, targetId) => {
    return (items || [])
      .filter((item) => item.id !== targetId)
      .map((item) => {
        if (item.replies && item.replies.length) {
          return {
            ...item,
            replies: removeItemById(item.replies, targetId),
          };
        }

        return item;
      });
  };

  const startEditingItem = (item) => {
    if (!canManageContent(item)) {
      setActionNotice("O prazo de 5 minutos para editar/ apagar este conteúdo já expirou.");
      return;
    }

    setEditingTargetId(item.id);
    setEditingText(item.text || "");
    setActionNotice("Modo de edição ativado. Lembre-se: o prazo total é de 5 minutos após a publicação.");
  };

  const cancelEditing = () => {
    setEditingTargetId(null);
    setEditingText("");
  };

  const saveEditedItem = (item) => {
    if (!item?.id) return;

    if (!canManageContent(item)) {
      setActionNotice("O prazo de 5 minutos para editar este conteúdo já expirou.");
      cancelEditing();
      return;
    }

    const cleaned = editingText.trim();
    if (!cleaned) {
      setActionNotice("O texto não pode ficar vazio.");
      return;
    }

    const moderation = detectAutoModeration(cleaned);
    const moderatedText = moderation.status === "approved"
      ? cleaned
      : "[Conteúdo ocultado automaticamente pela moderação.]";

    const next = updateItemById(comments, item.id, (current) => ({
      ...current,
      text: moderatedText,
      moderation,
      editedAt: new Date().toISOString(),
    }));

    saveComments(next);
    syncCommentsToFirestore(next);
    cancelEditing();
    setActionNotice("Conteúdo editado com sucesso.");

    if (moderation.status !== "approved") {
      setModerationInfo("Seu conteúdo editado passou por moderação automática.");
    }
  };

  const deleteItem = (item) => {
    if (!item?.id) return;

    const confirmed = window.confirm("Tem certeza que deseja apagar este conteúdo? Esta ação não pode ser desfeita.");
    if (!confirmed) return;

    if (!canManageContent(item)) {
      setActionNotice("O prazo de 5 minutos para apagar este conteúdo já expirou.");
      return;
    }

    const next = removeItemById(comments, item.id);
    saveComments(next);
    syncCommentsToFirestore(next);

    if (replyTo === item.id) {
      setReplyTo(null);
      setReplyText("");
    }

    if (editingTargetId === item.id) {
      cancelEditing();
    }

    setActionNotice("Conteúdo apagado com sucesso.");
  };

  const scoreFields = useMemo(() => [
    { key: "comunicacao", label: "Processo de Recrutamento" },
    { key: "etica", label: "Proposta salarial e benefícios" },
    { key: "cultura", label: "Visão e valores da empresa" },
    { key: "salario", label: "Data do Pagamento" },
    { key: "lideranca", label: "Acessibilidade e respeito da liderança" },
    { key: "estimacaoOrganizacao", label: "Condições de trabalho" },
    { key: "ambiente", label: "Estímulo ao respeito" },
    { key: "discriminacao", label: "Sofreu discriminação?" },
    { key: "diversidade", label: "Diversidade e Inclusão" },
    { key: "cargaHoraria", label: "Carga Horária / Jornada de Trabalho" },
    { key: "crescimento", label: "Oportunidades de Desenvolvimento / Crescimento" },
    { key: "rating", label: "Segurança e integridade" },
    { key: "saudeBemEstar", label: "Preocupação com o bem estar" },
    { key: "equilibrio", label: "Rotatividade" },
    { key: "reconhecimento", label: "Reconhecimento" },
    { key: "desenvolvimento", label: "Planos de cargos e salários" },
  ], []);

  const currentMetrics = useMemo(() => {
    const source = companyReviewCount > 0 ? companyAverages : (company || {});
    return Object.fromEntries(
      scoreFields.map((field) => {
        const value = Number(source?.[field.key]) || 0;
        return [field.key, value > 0 ? value : 0];
      })
    );
  }, [companyReviewCount, companyAverages, company, scoreFields]);

  React.useEffect(() => {
    let alive = true;

    const loadCompareSnapshot = async () => {
      if (!userIsPremium || !compareTargetSlug) {
        if (alive) {
          // Linha removida: setCompareError não é mais usado
        }
        return;
      }

      // Linha removida: setCompareLoading não é mais usado
      // Linha removida: setCompareError não é mais usado
      try {
        const reviews = await listReviewsByCompanySlug(compareTargetSlug, 300);
        if (!alive) return;

        const totals = Object.fromEntries(scoreFields.map((field) => [field.key, 0]));

        for (const review of reviews || []) {
          for (const field of scoreFields) {
            totals[field.key] += Number(review?.[field.key]) || 0;
          }
        }


      } catch (err) {
        if (!alive) return;
        // Linha removida: setCompareError não é mais usado
      } finally {
        if (!alive) return;
        // Linha removida: setCompareLoading não é mais usado
      }
    };

    loadCompareSnapshot();

    return () => {
      alive = false;
    };
  }, [userIsPremium, compareTargetSlug, compareOptions, scoreFields]);



  const premiumRadar = useMemo(() => {
    const entries = scoreFields
      .map((field) => ({
        key: field.key,
        label: field.label,
        value: Number(currentMetrics?.[field.key]) || 0,
      }))
      .filter((item) => item.value > 0);

    const strengths = [...entries].sort((a, b) => b.value - a.value).slice(0, 3);
    const risks = [...entries].sort((a, b) => a.value - b.value).slice(0, 3);

    const trendDelta = trendData.length >= 2
      ? Number(((trendData[trendData.length - 1]?.averages?.rating || 0) - (trendData[0]?.averages?.rating || 0)).toFixed(2))
      : 0;

    return { strengths, risks, trendDelta };
  }, [scoreFields, currentMetrics, trendData]);

  // Removidos: premiumAudienceLabel, premiumBenefitsByAudience pois não são mais usados

  // Removido: handleDownloadPremiumReport pois não é mais usado





  // Removido: companySourcePieData pois não é mais usado

  const visibleComments = useMemo(() => {
    const filterReplies = (replies) => {
      return (replies || []).filter((reply) => {
        if (!reply?.id) return false;
        if (isContentHidden(reply.id)) return false;
        if (isAuthorBlocked(reply.author)) return false;
        return true;
      });
    };

    return (comments || [])
      .filter((comment) => {
        if (!comment?.id) return false;
        if (isContentHidden(comment.id)) return false;
        if (isAuthorBlocked(comment.author)) return false;
        return true;
      })
      .map((comment) => ({
        ...comment,
        replies: filterReplies(comment.replies),
      }));
  }, [comments, isAuthorBlocked, isContentHidden]);

  const featuredComment = useMemo(() => {
    if (!Array.isArray(visibleComments) || visibleComments.length === 0) return null;

    return [...visibleComments].sort((a, b) => {
      const positiveDiff = getPositiveReactions(b) - getPositiveReactions(a);
      if (positiveDiff !== 0) return positiveDiff;
      return getTotalReactions(b) - getTotalReactions(a);
    })[0] || null;
  }, [visibleComments]);

  /* ── Pseudônimo clicável → /perfil/:profileId ── */
  const isAnonymousPseudonym = (value) => {
    const v = (value || "").toString().normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase().trim();
    return !v || v === "anonimo" || v === "annonimato";
  };

  const goToAuthorProfile = async (entry) => {
    const pseudonymRaw = (entry?.author || entry?.pseudonym || "").toString().trim();
    if (isAnonymousPseudonym(pseudonymRaw)) return;
    const existing = (entry?.authorProfileId || entry?.profileId || "").toString().trim();
    if (existing) {
      navigate(`/perfil/${encodeURIComponent(existing)}`);
      return;
    }
    try {
      const usersRef = collection(db, "users");
      const candidates = [
        query(usersRef, where("pseudonimo", "==", pseudonymRaw), limit(1)),
        query(usersRef, where("pseudonym", "==", pseudonymRaw), limit(1)),
      ];
      for (const q of candidates) {
        const snap = await getDocs(q);
        if (!snap.empty) {
          navigate(`/perfil/${encodeURIComponent(snap.docs[0].id)}`);
          return;
        }
      }
    } catch (err) {
      console.warn("Falha ao localizar perfil pelo pseudônimo:", err);
    }
  };

  const AuthorLink = ({ entry, className = "", prefix = "" }) => {
    const label = (entry?.author || entry?.pseudonym || "Anônimo").toString();
    const disabled = isAnonymousPseudonym(label);
    if (disabled) {
      return <p className={className}>{prefix}{label}</p>;
    }
    return (
      <button
        type="button"
        onClick={() => goToAuthorProfile(entry)}
        className={`${className} hover:underline focus:underline cursor-pointer text-left bg-transparent border-0 p-0`}
        title="Ver perfil público do avaliador"
      >
        {prefix}{label}
      </button>
    );
  };

  const renderCommentReplies = (replies = []) => {
    return (replies || []).map((reply) => (
        <div key={reply.id} className="bg-gray-50 dark:bg-slate-900 p-3 rounded-xl border border-gray-200 dark:border-slate-700">
          <div className="flex items-center justify-between">
            <AuthorLink entry={reply} className="text-sm font-semibold text-slate-900 dark:text-slate-100" />
            <div className="flex flex-wrap items-center justify-end gap-2">
              <p className="text-xs text-gray-500 dark:text-slate-400">
                {new Date(reply.createdAt).toLocaleString()}
                {reply.editedAt ? " (editado)" : ""}
              </p>
              <button
                type="button"
                onClick={() => setReplyTo(reply.id)}
                className="text-xs font-bold text-slate-700 dark:text-slate-200 hover:text-slate-900 dark:hover:text-white hover:underline"
              >
                Responder
              </button>
              <button
                type="button"
                onMouseDown={() => startReactionHold(`reply_${reply.id}`)}
                onMouseUp={clearReactionHoldTimer}
                onMouseLeave={clearReactionHoldTimer}
                onTouchStart={() => startReactionHold(`reply_${reply.id}`)}
                onTouchEnd={clearReactionHoldTimer}
                onTouchCancel={clearReactionHoldTimer}
                onClick={(e) => e.preventDefault()}
                className="h-10 w-10 rounded-full border border-gray-300 bg-gray-100 text-gray-700 hover:bg-gray-200 transition flex items-center justify-center text-lg"
                aria-label="Reagir à resposta"
                title="Segure para reagir"
              >
                🙂
              </button>
              {openReactionPickerId === `reply_${reply.id}` && (
                <div className="flex flex-wrap items-center gap-1">
                  {reactions.map((reaction) => {
                    const animKey = `${reply.id}__${reaction.key}`;
                    const isAnimated = animatedReactionKey === animKey;
                    const isSelected = getUserReactionFor(reply.id) === reaction.key;
                    return (
                      <button
                        key={reaction.key}
                        type="button"
                        onClick={() => handleReact(reply.id, reaction.key)}
                        aria-pressed={isSelected}
                        className={`flex items-center gap-2 px-3 py-1.5 border rounded-full transition-transform ${
                          isSelected
                            ? "border-blue-500 dark:border-blue-400 bg-blue-50 dark:bg-blue-900/40 ring-1 ring-blue-400"
                            : "border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-700 hover:bg-gray-100 dark:hover:bg-slate-600"
                        } ${isAnimated ? "reaction-burst" : ""}`}
                        aria-label={`Reagir com ${reaction.label}`}
                        title={isSelected ? "Clique para remover sua reação" : `Reagir com ${reaction.label}`}
                      >
                        <span className="text-base">{reaction.emoji}</span>
                        <span className={`text-sm font-semibold ${isSelected ? "text-blue-700 dark:text-blue-200" : "text-slate-700 dark:text-slate-100"}`}>
                          {reply.reactions?.[reaction.key] || 0}
                        </span>
                      </button>
                    );
                  })}
                </div>
              )}
              {canManageContent(reply) && (
                <>
                  <button
                    type="button"
                    onClick={() => startEditingItem(reply)}
                    className="text-xs font-bold text-slate-700 dark:text-slate-200 hover:text-slate-900 dark:hover:text-white hover:underline"
                  >
                    Editar
                  </button>
                  <button
                    type="button"
                    onClick={() => deleteItem(reply)}
                    className="text-xs font-bold text-slate-700 dark:text-slate-200 hover:text-slate-900 dark:hover:text-white hover:underline"
                  >
                    Apagar ({getRemainingManageTimeLabel(reply)})
                  </button>
                </>
              )}
              {!isOwnedByCurrentUser(reply) && (
                <button
                  type="button"
                  onClick={() =>
                    reportTarget({
                      targetId: reply.id,
                      targetType: "reply",
                      author: reply.author,
                      text: reply.text,
                    })
                  }
                  className="text-xs font-bold text-slate-700 dark:text-slate-200 hover:text-slate-900 dark:hover:text-white hover:underline"
                >
                  Denunciar conteúdo
                </button>
              )}
              {!isOwnedByCurrentUser(reply) && (
                <button
                  type="button"
                  onClick={() =>
                    reportTarget({
                      targetId: `user_${getAuthorBlockKey(reply.author)}`,
                      targetType: "user",
                      author: reply.author,
                      text: "",
                    })
                  }
                  className="text-xs font-bold text-slate-700 dark:text-slate-200 hover:text-slate-900 dark:hover:text-white hover:underline"
                >
                  Denunciar usuário
                </button>
              )}
            </div>
          </div>
          {editingTargetId === reply.id ? (
            <div className="mt-2 space-y-2">
              <textarea
                value={editingText}
                onChange={(e) => setEditingText(e.target.value)}
                className="w-full p-2 border border-blue-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                rows={2}
              />
              <div className="flex justify-end gap-2">
                <button
                  type="button"
                  onClick={cancelEditing}
                  className="px-3 py-1 text-xs font-semibold rounded-lg border border-slate-300 text-slate-700 hover:bg-slate-100"
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  onClick={() => saveEditedItem(reply)}
                  className="px-3 py-1 text-xs font-semibold rounded-lg bg-blue-600 text-white hover:bg-blue-700"
                >
                  Salvar edição
                </button>
              </div>
            </div>
          ) : (
            <p className="mt-1 text-sm text-slate-800 dark:text-slate-100">{reply.text}</p>
          )}

          {replyTo === reply.id && (
            <div className="mt-3 bg-gray-50 dark:bg-slate-900 p-3 rounded-xl border border-gray-200 dark:border-slate-700">
              <textarea
                value={replyText}
                onChange={(e) => setReplyText(e.target.value)}
                placeholder="Escreva sua resposta..."
                className="w-full p-2 border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                rows={2}
              />
              <div className="flex justify-end gap-2 mt-2">
                <button
                  type="button"
                  onClick={() => {
                    setReplyTo(null);
                    setReplyText("");
                  }}
                  className="px-3 py-1 text-sm font-semibold text-gray-600 dark:text-slate-300 rounded-lg hover:bg-gray-100 dark:hover:bg-slate-700"
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  onClick={() => handleReply(reply.id)}
                  className="px-3 py-1 bg-blue-600 text-white text-sm font-semibold rounded-lg hover:bg-blue-700"
                >
                  Enviar
                </button>
              </div>
            </div>
          )}

          {Array.isArray(reply.replies) && reply.replies.length > 0 && (
            <div className="mt-3 ml-3 space-y-3 border-l-2 border-blue-100 dark:border-slate-700 pl-3">
              {renderCommentReplies(reply.replies)}
            </div>
          )}
        </div>
      ));
  };


  if (!company) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-blue-100 flex items-center justify-center p-6">
        <div className="bg-white rounded-3xl shadow-xl p-8 border border-blue-100 max-w-lg text-center">
          <h1 className="text-2xl font-bold text-blue-800 dark:text-slate-100 mb-4">Empresa não encontrada</h1>
          <p className="text-sm text-slate-600 mb-6">
            Não foi possível localizar a empresa informada. Volte à página inicial e selecione outra empresa.
          </p>
          <button
            onClick={() => navigate("/")}
            className="px-6 py-3 bg-blue-600 text-white font-semibold rounded-xl hover:bg-blue-700 transition"
          >
            Voltar para início
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-blue-100 dark:from-slate-950 dark:to-slate-900 flex flex-col">
      <ConflictDeclarationGate companyName={company?.company} />
      <AppHeader theme={theme} toggleTheme={toggleTheme} />

      {/* ═══ LINHA 2 — Logo empresa + nome ═══ */}
      <div className="w-full bg-white/80 dark:bg-slate-800/70 border-b border-blue-100 dark:border-slate-700">
        <div className="max-w-5xl mx-auto flex items-center gap-4 px-4 py-4">
          {/* Esquerda: logo + nome + setor + localização */}
          <div className="flex items-center gap-4 min-w-0">
            <div className="w-14 h-14 rounded-2xl overflow-hidden border border-blue-200 dark:border-slate-600 bg-white flex-shrink-0">
              {companyLogo ? (
                <img
                  src={companyLogo}
                  alt={`Logo ${company.company}`}
                  className="w-full h-full object-contain p-1"
                  onError={(e) => {
                    if (logoIndex < logoCandidates.length - 1) {
                      setLogoIndex((prev) => prev + 1);
                    } else {
                      e.target.onerror = null;
                      e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(companyInitials)}&background=1a237e&color=fff&size=128&bold=true`;
                    }
                  }}
                />
              ) : (
                <div
                  className="w-full h-full flex items-center justify-center bg-blue-900 text-white font-extrabold text-lg select-none"
                  aria-label={`Logo ${company.company}`}
                >
                  {companyInitials}
                </div>
              )}
            </div>
            <div className="min-w-0">
              <h1 className="text-lg font-extrabold text-blue-800 dark:text-slate-100 leading-tight truncate">{company.company}</h1>
              {companyInfo?.sector && companyInfo.sector !== "Não identificado automaticamente" && (
                <p className="text-sm text-slate-600 dark:text-slate-300 truncate">{companyInfo.sector}</p>
              )}
              {companyInfo?.location && companyInfo.location !== "Não identificado automaticamente" && (
                <p className="text-xs text-slate-500 dark:text-slate-400 truncate">{companyInfo.location}</p>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Bloco de cadastro/verificação de empresa */}
      <div className="w-full bg-transparent">
        <div className="max-w-5xl mx-auto px-4">
          {(() => {
            // Oculta o convite "Cadastrar empresa" para usuários trabalhadores,
            // evitando confusão com funcionalidades destinadas a empresas.
            let viewerType = "";
            try {
              const p = JSON.parse(localStorage.getItem("userProfile") || "{}");
              viewerType = String(p?.userType || "").toLowerCase();
            } catch {}
            const isWorkerViewer = viewerType === "trabalhador";
            if (isWorkerViewer) return null;
            return company && (typeof company.verified === 'undefined' || company.verified === false) ? (
            <div className="flex items-center justify-between bg-blue-50 dark:bg-slate-900 border border-blue-100 dark:border-slate-700 rounded-lg px-4 py-2 mt-2 mb-2">
              <span className="text-xs text-blue-900 dark:text-blue-200">
                Esta é a sua empresa? Cadastre-se gratuitamente até 31/07/2026 e gerencie este perfil.
              </span>
              <button
                type="button"
                className="ml-4 px-3 py-1 text-xs font-semibold bg-blue-600 text-white rounded hover:bg-blue-700 transition"
                onClick={() => navigate('/empresa/cadastro')}
              >
                Cadastrar empresa
              </button>
            </div>
          ) : null;
          })()}
          {company && company.verified === true ? (
            <div className="flex items-center gap-2 bg-emerald-50 dark:bg-emerald-900 border border-emerald-200 dark:border-emerald-700 rounded-lg px-4 py-2 mt-2 mb-2">
              <span className="inline-flex items-center text-xs text-emerald-800 dark:text-emerald-200 font-semibold">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1 text-emerald-600" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                Empresa Verificada
              </span>
            </div>
          ) : null}
        </div>
      </div>

      {/* ═══ NAVEGAÇÃO DE ABAS ═══ */}
      <div className="w-full bg-white/70 dark:bg-slate-800/40 border-b border-blue-100 dark:border-slate-700">
        <div className="max-w-5xl mx-auto px-4">
          <nav className="flex gap-2 overflow-x-auto" role="tablist" aria-label="Seções do perfil da empresa">
            {[
              { key: "overview", label: "Visão Geral" },
              { key: "selectionProcess", label: "Processo Seletivo" },
            ].map((tab) => {
              const isActive = activeTab === tab.key;
              return (
                <button
                  key={tab.key}
                  type="button"
                  role="tab"
                  aria-selected={isActive}
                  onClick={() => setActiveTab(tab.key)}
                  className={`px-4 py-2.5 text-sm font-semibold whitespace-nowrap border-b-2 transition ${
                    isActive
                      ? "border-blue-600 text-blue-700 dark:text-blue-300"
                      : "border-transparent text-slate-600 dark:text-slate-300 hover:text-blue-600 dark:hover:text-blue-300"
                  }`}
                >
                  {tab.label}
                </button>
              );
            })}
          </nav>
        </div>
      </div>

      {activeTab === "selectionProcess" ? (
        <SelectionProcessOverview company={company} />
      ) : (
        <>

      {/* ═══ LINHA 3 — Banner Premium (só p/ não-premium e não-admin) ═══ */}
      {!userIsPremium && !isAdmin() && (
        <div className="w-full bg-gradient-to-r from-amber-600 to-yellow-500 dark:from-amber-800 dark:to-yellow-700">
          <div className="max-w-5xl mx-auto flex items-center justify-between px-4 py-3">
            <p className="text-sm font-semibold text-white">
              Desbloqueie análises completas com o <span className="font-bold">Plano Premium</span>
            </p>
            <button
              type="button"
              onClick={() => navigate("/escolha-perfil?planos=1")}
              className="shrink-0 px-4 py-1.5 text-sm font-bold text-amber-800 bg-white rounded-lg hover:bg-amber-50 transition"
            >
              Saiba mais
            </button>
          </div>
        </div>
      )}

      {/* ═══ LINHA 4 — Botão Ver Avaliações centralizado ═══ */}
      <div className="w-full bg-blue-50/50 dark:bg-slate-900/50 border-b border-blue-100 dark:border-slate-700">
        <div className="max-w-5xl mx-auto flex justify-center px-4 py-4">
          {(() => {
            const userCommented = comments.some((c) => normalizeKey(c?.author) === normalizeKey(getCurrentPseudonym()));
            if (userCommented) {
              return (
                <button
                  type="button"
                  onClick={() => {
                    const el = document.getElementById("secao-comentarios");
                    if (el) el.scrollIntoView({ behavior: "smooth" });
                  }}
                  className="px-6 py-2.5 bg-emerald-600 text-white font-bold rounded-xl hover:bg-emerald-700 transition text-sm"
                >
                  Ver minha avaliação
                </button>
              );
            }
            return (
              <button
                type="button"
                onClick={() => {
                  const el = document.getElementById("secao-comentarios");
                  if (el) el.scrollIntoView({ behavior: "smooth" });
                }}
                className={`px-6 py-2.5 font-bold rounded-xl transition text-sm ${
                  userIsCertified
                    ? "bg-blue-600 text-white hover:bg-blue-700"
                    : "bg-slate-300 text-slate-700 hover:bg-slate-400 dark:bg-slate-700 dark:text-slate-200"
                }`}
                title={
                  userIsCertified
                    ? "Publicar sua avaliação desta empresa"
                    : "Apenas usuários com Selo de Perfil Verificado podem avaliar. Conclua seu perfil para liberar."
                }
              >
                {userIsCertified ? "Avaliar esta empresa" : "Avaliar (verificação necessária)"}
              </button>
            );
          })()}
        </div>
      </div>

      {/* ═══ Conteúdo principal ═══ */}
      <div className="flex-1 px-4 pt-6 pb-10">
      <div className="max-w-4xl mx-auto bg-white dark:bg-slate-800 rounded-3xl shadow-xl border border-blue-100 dark:border-slate-700 p-8">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
          <div className="flex items-center gap-4">
            <div>
              <p className={`text-lg font-bold ${getScoreColor(average)}`}>
                {average === "--" ? "--" : `${average} / 5`}
              </p>
              <p className="text-xs text-gray-500">
                {evaluationCount} avaliação{evaluationCount === 1 ? "" : "es"}
                {companyCommentTotal > 0
                  ? ` · ${companyCommentTotal} comentário${companyCommentTotal === 1 ? "" : "s"}`
                  : ""}
              </p>
            </div>
          </div>
          <div className="flex flex-col sm:flex-row gap-3 w-full md:w-auto">
            <button
              type="button"
              onClick={openLinkedInJobs}
              className="px-6 py-3 bg-slate-200 hover:bg-slate-300 text-slate-800 font-bold rounded-xl transition"
            >
              Ver vagas no LinkedIn
            </button>
            {hasAverageScore && (
              <div
                className={`px-4 py-2 rounded-xl border font-bold text-sm flex flex-col items-center justify-center leading-tight ${
                  isRecommendedCompany
                    ? "bg-emerald-50 border-emerald-200 text-emerald-700"
                    : "bg-red-50 border-red-200 text-red-700"
                }`}
              >
                <span className="text-xl" aria-hidden="true">{isRecommendedCompany ? "✓" : "X"}</span>
                <span>{isRecommendedCompany ? "Acima da média" : "Abaixo da média"}</span>
              </div>
            )}
          </div>
        </div>

        {userHasResumeProof && (
          <div className="mt-4 bg-emerald-50 border border-emerald-200 rounded-xl p-4">
            <p className="text-emerald-800 font-bold">
              Experiência comprovada: esta empresa aparece no currículo carregado pelo profissional.
            </p>
          </div>
        )}

        <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-6">
          {scoreFields.map((field) => {
            const rawValue = companyReviewCount > 0
              ? companyAverages[field.key]
              : company[field.key];
            const hasScore = typeof rawValue === "number" && rawValue > 0;
            const hasComments = (itemCommentCounts[field.key] || 0) > 0;
            // Exibe o critério se ele tem nota OU comentários — assim todos os
            // critérios avaliados/comentados por um usuário ficam visíveis.
            if (!hasScore && !hasComments) return null;
            const value = hasScore ? rawValue : 0;
            return (
              <div key={field.key} className="bg-gray-50 dark:bg-slate-900 p-4 rounded-xl border border-gray-200 dark:border-slate-700">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-sm font-semibold text-gray-700 dark:text-slate-100">{field.label}</p>
                  <span className={`px-2 py-1 rounded-full text-xs font-bold ${getScoreColor(value)}`}>
                    {hasScore ? value.toFixed(1) : "—"}
                  </span>
                </div>
                <div className="h-2 bg-gray-200 dark:bg-slate-800 rounded-full overflow-hidden">
                  <div
                    className={`${getScoreColor(value)} h-full rounded-full`}
                    style={{ width: `${hasScore ? Math.min(100, (value / 5) * 100) : 0}%` }}
                  />
                </div>
                <button
                  type="button"
                  onClick={() => navigate(`/empresa/comentarios-item?name=${encodeURIComponent(company.company)}&item=${field.key}`)}
                  className="mt-3 text-xs font-medium text-[#1a237e] dark:text-blue-300 hover:text-blue-900 dark:hover:text-white hover:underline focus:underline transition-colors"
                >
                  Ver comentários deste item ({itemCommentCounts[field.key] || 0})
                </button>
              </div>
            );
          })}
        </div>

        <div className="clear-both" />

        {/* ── DASHBOARD PREMIUM (admin_empresa / is_premium) ── */}
        {userIsPremium ? (
          <div className="mt-8 bg-gradient-to-br from-indigo-50 to-purple-50 rounded-2xl shadow-sm p-6 border border-indigo-200">
            <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
              <div>
                <span className="text-xs font-bold uppercase tracking-widest text-indigo-600 bg-indigo-100 border border-indigo-200 rounded-full px-3 py-1">
                  {userRole === "admin_empresa" ? "Admin Empresa" : "Premium"}
                </span>
                <h2 className="mt-2 text-lg font-bold text-indigo-900">Dashboard Detalhado</h2>
                <p className="text-xs text-indigo-600">
                  Tendência de cada quesito ao longo do tempo com filtro por período.
                </p>
              </div>
              <button
                type="button"
                onClick={() => setDashboardVisible((v) => !v)}
                className="px-4 py-2 bg-indigo-600 text-white text-sm font-bold rounded-xl hover:bg-indigo-700 transition"
              >
                {dashboardVisible ? "Fechar Dashboard" : "Abrir Dashboard"}
              </button>
            </div>

            {dashboardVisible && (
              <>
                {/* Filtros de período */}
                <div className="flex flex-wrap gap-3 mb-5 items-end">
                  <div>
                    <label className="text-xs font-semibold text-indigo-700 block mb-1">De</label>
                    <input
                      type="date"
                      value={periodStart}
                      onChange={(e) => setPeriodStart(e.target.value)}
                      className="p-2 border border-indigo-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400"
                    />
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-indigo-700 block mb-1">Até</label>
                    <input
                      type="date"
                      value={periodEnd}
                      onChange={(e) => setPeriodEnd(e.target.value)}
                      className="p-2 border border-indigo-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400"
                    />
                  </div>
                  <button
                    type="button"
                    onClick={fetchTrend}
                    disabled={trendLoading}
                    className="px-4 py-2 bg-indigo-600 text-white text-sm font-bold rounded-xl hover:bg-indigo-700 transition disabled:opacity-60"
                  >
                    {trendLoading ? "Buscando..." : "Aplicar filtro"}
                  </button>
                  {(periodStart || periodEnd) && (
                    <button
                      type="button"
                      onClick={() => { setPeriodStart(""); setPeriodEnd(""); }}
                      className="px-3 py-2 text-xs text-indigo-700 border border-indigo-200 rounded-xl hover:bg-indigo-100 transition"
                    >
                      Limpar
                    </button>
                  )}
                </div>

                {/* Seletor de quesito */}
                <div className="mb-4">
                  <label className="text-xs font-semibold text-indigo-700 block mb-1">Quesito em foco</label>
                  <select
                    value={selectedTrendKey}
                    onChange={(e) => setSelectedTrendKey(e.target.value)}
                    className="p-2 border border-indigo-300 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-indigo-400"
                  >
                    {scoreFields.map((f) => (
                      <option key={f.key} value={f.key}>{f.label}</option>
                    ))}
                  </select>
                </div>

                {trendError && (
                  <p className="text-sm text-red-600 mb-3">{trendError}</p>
                )}

                {trendData.length === 0 && !trendLoading && !trendError && (
                  <p className="text-sm text-indigo-500">Nenhum dado encontrado para o período selecionado.</p>
                )}

                {trendData.length > 0 && (() => {
                  const maxVal = Math.max(...trendData.map((d) => d.averages?.[selectedTrendKey] || 0), 1);
                  return (
                    <div className="space-y-2">
                      {trendData.map((row) => {
                        const val = row.averages?.[selectedTrendKey] || 0;
                        const pct = maxVal > 0 ? (val / 5) * 100 : 0;
                        const barColor =
                          val >= 4 ? "bg-emerald-500" :
                          val >= 3 ? "bg-lime-500" :
                          val >= 2 ? "bg-yellow-500" : "bg-red-500";
                        return (
                          <div key={row.month} className="flex items-center gap-3">
                            <span className="text-xs font-mono text-indigo-700 w-16 shrink-0">{row.month}</span>
                            <div className="flex-1 h-4 bg-indigo-100 rounded-full overflow-hidden">
                              <div
                                className={`${barColor} h-full rounded-full transition-all`}
                                style={{ width: `${pct.toFixed(1)}%` }}
                              />
                            </div>
                            <span className="text-xs font-bold text-indigo-800 w-8 text-right">{val.toFixed(1)}</span>
                            <span className="text-xs text-indigo-500">({row.count} av.)</span>
                          </div>
                        );
                      })}
                    </div>
                  );
                })()}

                {trendData.length > 0 && (() => {
                  const last = trendData[trendData.length - 1];
                  return (
                    <div className="mt-6">
                      <p className="text-xs font-bold text-indigo-700 mb-2 uppercase tracking-wide">
                        Médias por quesito — {last.month} ({last.count} avaliações)
                      </p>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                        {scoreFields.map((f) => {
                          const v = last.averages?.[f.key] || 0;
                          return (
                            <div key={f.key} className="flex justify-between items-center bg-white rounded-lg px-3 py-2 border border-indigo-100">
                              <span className="text-xs text-slate-700">{f.label}</span>
                              <span className={`text-xs font-bold ${getScoreColor(v)}`}>{v > 0 ? v.toFixed(1) : "—"}</span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })()}
              </>
            )}
          </div>
        ) : (
          <div className="mt-8 bg-gradient-to-br from-slate-50 to-indigo-50 dark:from-slate-800 dark:to-slate-900 rounded-2xl p-6 border border-indigo-200 dark:border-slate-700 flex items-center gap-4">
            <div className="text-3xl">🔒</div>
            <div>
              <p className="font-bold text-indigo-800 dark:text-indigo-200 text-sm">Dashboard Detalhado — Premium</p>
              <p className="text-xs text-indigo-600 dark:text-indigo-300 mt-0.5">
                {userIsLoggedIn
                  ? "Assine o Premium Trabalhador para desbloquear o Dashboard Detalhado."
                  : "Faça login para ver mais."}
              </p>
            </div>
          </div>
        )}

        <div className="mt-8 bg-white dark:bg-slate-900 rounded-2xl shadow-sm p-6 border border-blue-100 dark:border-slate-700">
          <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
            <div>
              <h2 className="text-lg font-bold text-blue-800 dark:text-blue-200">Radar de decisão Premium</h2>
              <p className="text-xs text-slate-600 dark:text-slate-400">Resumo prático para apoiar sua decisão de candidatura.</p>
            </div>
            {!userIsPremium && (
              <button
                type="button"
                onClick={userIsLoggedIn ? () => handlePremiumUnlock("worker") : () => navigate("/")}
                disabled={userIsLoggedIn && !!checkoutLoadingAudience}
                className="px-4 py-2 rounded-xl bg-blue-600 text-white text-sm font-semibold hover:bg-blue-700 disabled:opacity-70"
              >
                {userIsLoggedIn
                  ? (checkoutLoadingAudience === "worker" ? "Abrindo checkout..." : "Assine Premium Trabalhador")
                  : "Faça login para ver mais"}
              </button>
            )}
          </div>

          <div className={userIsPremium ? "" : "pointer-events-none select-none blur-[5px] opacity-80"}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="rounded-xl border border-emerald-200 dark:border-emerald-700/40 bg-emerald-50 dark:bg-emerald-900/20 p-4">
                <p className="text-xs font-bold uppercase tracking-wide text-emerald-700 dark:text-emerald-300">Pontos fortes</p>
                <ul className="mt-2 space-y-1 text-sm text-emerald-900 dark:text-emerald-100">
                  {premiumRadar.strengths.map((item) => (
                    <li key={`strength_${item.key}`}>{item.label}: {item.value.toFixed(1)}</li>
                  ))}
                </ul>
              </div>
              <div className="rounded-xl border border-rose-200 dark:border-rose-700/40 bg-rose-50 dark:bg-rose-900/20 p-4">
                <p className="text-xs font-bold uppercase tracking-wide text-rose-700 dark:text-rose-300">Pontos de atenção</p>
                <ul className="mt-2 space-y-1 text-sm text-rose-900 dark:text-rose-100">
                  {premiumRadar.risks.map((item) => (
                    <li key={`risk_${item.key}`}>{item.label}: {item.value.toFixed(1)}</li>
                  ))}
                </ul>
              </div>
            </div>

            <div className="mt-4 rounded-xl border border-indigo-200 dark:border-indigo-700/40 bg-indigo-50 dark:bg-indigo-900/20 p-4">
              <p className="text-xs font-bold uppercase tracking-wide text-indigo-700 dark:text-indigo-300">Sinal de tendência</p>
              <p className="mt-1 text-sm text-indigo-900 dark:text-indigo-100">
                {trendData.length >= 2
                  ? `No período analisado, a métrica de segurança e integridade variou ${premiumRadar.trendDelta >= 0 ? "+" : ""}${premiumRadar.trendDelta.toFixed(2)} ponto(s).`
                  : "Ative o Dashboard Detalhado para capturar tendência mensal e enriquecer este radar."}
              </p>
            </div>
          </div>
        </div>

        <div className="mt-8 bg-white dark:bg-slate-900 rounded-2xl shadow-sm p-6 border border-blue-100 dark:border-slate-700">
          <h2 className="text-lg font-bold text-[#1a237e] dark:text-blue-300 font-azonix tracking-[0.08em] mb-4">Dados Oficiais da Empresa</h2>
          <div className="bg-blue-50 dark:bg-slate-800 rounded-xl p-4 border border-blue-100 dark:border-slate-700 grid gap-3 md:grid-cols-2">
            <div>
              <p className="text-xs font-semibold text-[#1a237e] dark:text-blue-300 uppercase tracking-wide">CNPJ</p>
              <p className="text-sm text-slate-800 dark:text-slate-200 font-medium">{companyInfo?.cnpjFormatted}</p>
            </div>
            <div>
              <p className="text-xs font-semibold text-[#1a237e] dark:text-blue-300 uppercase tracking-wide">Razão Social</p>
              <p className="text-sm text-slate-800 dark:text-slate-200 font-medium">{companyInfo?.razaoSocial}</p>
            </div>
            <div>
              <p className="text-xs font-semibold text-[#1a237e] dark:text-blue-300 uppercase tracking-wide">Nome Fantasia</p>
              <p className="text-sm text-slate-800 dark:text-slate-200 font-medium">{companyInfo?.nomeFantasia}</p>
            </div>
            <div className="md:col-span-2">
              <p className="text-xs font-semibold text-[#1a237e] dark:text-blue-300 uppercase tracking-wide">Endereço</p>
              <p className="text-sm text-slate-800 dark:text-slate-200 font-medium">{companyInfo?.endereco}</p>
            </div>
          </div>
        </div>

        <div className="mt-8 bg-white dark:bg-slate-900 rounded-2xl shadow-sm p-6 border border-blue-100 dark:border-slate-700">
          <h2 className="text-lg font-bold text-[#1a237e] dark:text-blue-300 font-azonix tracking-[0.08em] mb-4">Sobre a empresa</h2>
          <div className="bg-blue-50 dark:bg-slate-800 rounded-xl p-4 border border-blue-100 dark:border-slate-700 space-y-3">
            {insightsLoading && (
              <p className="text-sm text-[#1a237e] dark:text-blue-300 font-semibold">Buscando dados automáticos da empresa...</p>
            )}
            <div>
              <p className="text-xs font-semibold text-[#1a237e] dark:text-blue-300 uppercase tracking-wide">Descrição Automática</p>
              <p className="text-sm text-slate-800 dark:text-slate-200 font-medium">{companyInfo?.description}</p>
              {companyInfo?.description?.includes("Não identificado") && (
                <button className="mt-1 text-xs text-blue-600 underline" onClick={() => alert('Sugestão: envie um email para contato@trabalheila.com.br com as informações da empresa!')}>Sugerir informações desta empresa</button>
              )}
            </div>
            <div>
              <p className="text-xs font-semibold text-[#1a237e] dark:text-blue-300 uppercase tracking-wide">Ramo</p>
              <p className="text-sm text-slate-800 dark:text-slate-200 font-medium">{companyInfo?.sector}</p>
              {companyInfo?.sector?.includes("Não identificado") && (
                <button className="mt-1 text-xs text-blue-600 underline" onClick={() => alert('Sugestão: envie um email para contato@trabalheila.com.br com o ramo da empresa!')}>Sugerir informações desta empresa</button>
              )}
            </div>
            <div>
              <p className="text-xs font-semibold text-[#1a237e] dark:text-blue-300 uppercase tracking-wide">Localização</p>
              <p className="text-sm text-slate-800 dark:text-slate-200 font-medium">{companyInfo?.location}</p>
              {companyInfo?.location?.includes("Não identificado") && (
                <button className="mt-1 text-xs text-blue-600 underline" onClick={() => alert('Sugestão: envie um email para contato@trabalheila.com.br com a localização da empresa!')}>Sugerir informações desta empresa</button>
              )}
            </div>
            <div>
              <p className="text-xs font-semibold text-[#1a237e] dark:text-blue-300 uppercase tracking-wide">CNPJ</p>
              <p className="text-sm text-slate-800 dark:text-slate-200 font-medium">{companyInfo?.cnpj}</p>
            </div>
            <div>
              <p className="text-xs font-semibold text-[#1a237e] dark:text-blue-300 uppercase tracking-wide">Site</p>
              <p className="text-sm text-slate-800 dark:text-slate-200 font-medium break-all">{companyInfo?.website}</p>
              {companyInfo?.website?.includes("Não identificado") && (
                <button className="mt-1 text-xs text-blue-600 underline" onClick={() => alert('Sugestão: envie um email para contato@trabalheila.com.br com o site da empresa!')}>Sugerir informações desta empresa</button>
              )}
            </div>
            <div>
              <p className="text-xs font-semibold text-[#1a237e] dark:text-blue-300 uppercase tracking-wide">Redes sociais</p>
              <div className="mt-1 space-y-1">
                {(companyInfo?.socialLinks || []).map((item) => (
                  <div key={item.label}>
                    <p className="text-sm text-slate-800 dark:text-slate-200 font-medium">
                      {item.label}: {item.value}
                    </p>
                    {item.value?.includes("Não identificado") && (
                      <button className="mt-1 text-xs text-blue-600 underline" onClick={() => alert(`Sugestão: envie um email para contato@trabalheila.com.br com o link do ${item.label}!`)}>Sugerir informações desta empresa</button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        <RelevantWorkersSection
          companySlug={getCompanySlug()}
          companyName={
            company?.razaoSocial ||
            company?.nomeFantasia ||
            company?.company ||
            ""
          }
          companySector={
            company?.ramo ||
            company?.setor ||
            company?.segmento ||
            company?.industry ||
            ""
          }
        />

        <div id="secao-comentarios" className="mt-8 bg-white dark:bg-slate-900 rounded-2xl shadow-sm p-6 border border-blue-100 dark:border-slate-700">
          <h2 className="text-lg font-bold text-blue-800 dark:text-blue-200 font-azonix tracking-[0.08em] mb-4">Comentários</h2>
          <div className="space-y-4">
            <p className="text-xs text-blue-700 dark:text-blue-200 bg-blue-50 dark:bg-slate-800 border border-blue-200 dark:border-slate-700 rounded-lg px-3 py-2">
              Após publicar, você pode editar ou apagar seu comentário/resposta por até 5 minutos.
            </p>

            {!userIsCertified ? (
              <div className="rounded-xl border border-amber-300 bg-amber-50 dark:bg-amber-900/30 dark:border-amber-600 px-4 py-3 text-sm text-amber-900 dark:text-amber-100 flex items-start gap-3">
                <svg
                  aria-hidden="true"
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                  className="w-5 h-5 flex-shrink-0 text-amber-500 dark:text-amber-300 mt-0.5"
                >
                  <path d="M12 2L2 22h20L12 2zm0 6l6.5 11h-13L12 8zm-1 4v4h2v-4h-2zm0 5v2h2v-2h-2z" />
                </svg>
                <div className="space-y-2">
                  <p>
                    Para manter a legitimidade das avaliações, apenas usuários com <strong>perfil verificado</strong> podem avaliar empresas. Conclua seu perfil e verifique suas experiências para liberar esta funcionalidade.
                  </p>
                  <p className="text-xs text-amber-800/80 dark:text-amber-200/80">
                    Você ainda pode explorar todas as notas e detalhes desta empresa e cadastrar novas empresas na plataforma.
                  </p>
                  <button
                    type="button"
                    onClick={() => navigate("/pseudonym")}
                    className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-amber-600 text-white text-xs font-semibold hover:bg-amber-700 transition"
                  >
                    Concluir verificação do perfil →
                  </button>
                </div>
              </div>
            ) : (
              <div className="flex flex-col gap-2">
                <label className="text-sm font-semibold text-slate-700">
                  Você trabalhou lá? Quer compartilhar sua experiência? Digite aqui.
                </label>
                <textarea
                  value={mandatoryComment}
                  onChange={(e) => {
                    setMandatoryComment(e.target.value);
                    if (commentError) setCommentError("");
                  }}
                  placeholder="Escreva um comentário sobre essa empresa..."
                  className={`w-full p-3 border rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 ${commentError ? "border-red-400" : "border-gray-200"}`}
                  rows={3}
                  required
                />
                {commentError && <p className="text-sm text-red-600">{commentError}</p>}

                <button
                  type="button"
                  onClick={handleAddComment}
                  className="self-end px-5 py-2 bg-blue-600 text-white font-semibold rounded-xl hover:bg-blue-700 transition"
                >
                  Publicar comentário
                </button>
              </div>
            )}

            {moderationInfo && (
              <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                {moderationInfo}
              </p>
            )}

            {actionNotice && (
              <p className="text-sm text-blue-700 bg-blue-50 border border-blue-200 rounded-lg px-3 py-2">
                {actionNotice}
              </p>
            )}

            <div className="bg-gray-50 border border-gray-200 rounded-xl p-4">
              <p className="text-sm font-semibold text-blue-800 font-azonix mb-2">Comentário mais bem avaliado</p>
              {featuredComment ? (
                <>
                  <p className="text-sm text-slate-800 italic whitespace-pre-line">"{featuredComment.text}"</p>
                  <div className="flex justify-end mt-3">
                    <AuthorLink entry={featuredComment} className="text-xs text-slate-600" prefix="- " />
                  </div>
                </>
              ) : (
                <p className="text-sm text-slate-500">--</p>
              )}
            </div>

            {visibleComments.length === 0 ? (
              <p className="text-center text-gray-500">Seja o primeiro a comentar sobre esta empresa.</p>
            ) : (
              [...visibleComments]
                .sort((a, b) => getTotalReactions(b) - getTotalReactions(a))
                .map((comment) => (
                  <div key={comment.id} className="bg-white dark:bg-slate-800 p-4 rounded-xl border border-gray-200 dark:border-slate-700">
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <AuthorLink entry={comment} className="font-semibold text-sm text-slate-900 dark:text-slate-100" />
                        <p className="text-xs text-gray-500 dark:text-slate-400">
                          {new Date(comment.createdAt).toLocaleString()}
                          {comment.editedAt ? " (editado)" : ""}
                        </p>
                      </div>
                      <div className="flex flex-wrap items-center justify-end gap-2">
                        <button
                          type="button"
                          onClick={() => setReplyTo(comment.id)}
                          className="text-xs font-bold text-slate-700 dark:text-slate-200 hover:text-slate-900 dark:hover:text-white hover:underline"
                        >
                          Responder
                        </button>
                        <button
                          type="button"
                          onMouseDown={() => startReactionHold(`comment_${comment.id}`)}
                          onMouseUp={clearReactionHoldTimer}
                          onMouseLeave={clearReactionHoldTimer}
                          onTouchStart={() => startReactionHold(`comment_${comment.id}`)}
                          onTouchEnd={clearReactionHoldTimer}
                          onTouchCancel={clearReactionHoldTimer}
                          onClick={(e) => e.preventDefault()}
                          className="h-10 w-10 rounded-full border border-gray-300 bg-gray-100 text-gray-700 hover:bg-gray-200 transition flex items-center justify-center text-lg"
                          aria-label="Reagir ao comentário"
                          title="Segure para reagir"
                        >
                          🙂
                        </button>
                        {openReactionPickerId === `comment_${comment.id}` && (
                          <div className="flex flex-wrap items-center gap-1">
                            {reactions.map((reaction) => {
                              const animKey = `${comment.id}__${reaction.key}`;
                              const isAnimated = animatedReactionKey === animKey;
                              const isSelected = getUserReactionFor(comment.id) === reaction.key;
                              return (
                                <button
                                  key={reaction.key}
                                  type="button"
                                  onClick={() => handleReact(comment.id, reaction.key)}
                                  aria-pressed={isSelected}
                                  className={`flex items-center gap-2 px-3 py-1.5 border rounded-full transition-transform ${
                                    isSelected
                                      ? "border-blue-500 dark:border-blue-400 bg-blue-50 dark:bg-blue-900/40 ring-1 ring-blue-400"
                                      : "border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-700 hover:bg-gray-100 dark:hover:bg-slate-600"
                                  } ${isAnimated ? "reaction-burst" : ""}`}
                                  aria-label={`Reagir com ${reaction.label}`}
                                  title={isSelected ? "Clique para remover sua reação" : `Reagir com ${reaction.label}`}
                                >
                                  <span className="text-base">{reaction.emoji}</span>
                                  <span className={`text-sm font-semibold ${isSelected ? "text-blue-700 dark:text-blue-200" : "text-slate-700 dark:text-slate-100"}`}>
                                    {comment.reactions?.[reaction.key] || 0}
                                  </span>
                                </button>
                              );
                            })}
                          </div>
                        )}
                        {canManageContent(comment) && (
                          <>
                            <button
                              type="button"
                              onClick={() => startEditingItem(comment)}
                              className="text-xs font-bold text-slate-700 dark:text-slate-200 hover:text-slate-900 dark:hover:text-white hover:underline"
                            >
                              Editar
                            </button>
                            <button
                              type="button"
                              onClick={() => deleteItem(comment)}
                              className="text-xs font-bold text-slate-700 dark:text-slate-200 hover:text-slate-900 dark:hover:text-white hover:underline"
                            >
                              Apagar ({getRemainingManageTimeLabel(comment)})
                            </button>
                          </>
                        )}
                        {!isOwnedByCurrentUser(comment) && (
                          <button
                            type="button"
                            onClick={() =>
                              reportTarget({
                                targetId: comment.id,
                                targetType: "comment",
                                author: comment.author,
                                text: comment.text,
                              })
                            }
                            className="text-xs font-bold text-slate-700 dark:text-slate-200 hover:text-slate-900 dark:hover:text-white hover:underline"
                          >
                            Denunciar conteúdo
                          </button>
                        )}
                        {!isOwnedByCurrentUser(comment) && (
                          <button
                            type="button"
                            onClick={() =>
                              reportTarget({
                                targetId: `user_${getAuthorBlockKey(comment.author)}`,
                                targetType: "user",
                                author: comment.author,
                                text: "",
                              })
                            }
                            className="text-xs font-bold text-slate-700 dark:text-slate-200 hover:text-slate-900 dark:hover:text-white hover:underline"
                          >
                            Denunciar usuário
                          </button>
                        )}
                      </div>
                    </div>

                    {editingTargetId === comment.id ? (
                      <div className="mt-3 space-y-2">
                        <textarea
                          value={editingText}
                          onChange={(e) => setEditingText(e.target.value)}
                          className="w-full p-3 border border-blue-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500"
                          rows={3}
                        />
                        <div className="flex justify-end gap-2">
                          <button
                            type="button"
                            onClick={cancelEditing}
                            className="px-3 py-1 text-xs font-semibold rounded-lg border border-slate-300 text-slate-700 hover:bg-slate-50"
                          >
                            Cancelar
                          </button>
                          <button
                            type="button"
                            onClick={() => saveEditedItem(comment)}
                            className="px-3 py-1 text-xs font-semibold rounded-lg bg-blue-600 text-white hover:bg-blue-700"
                          >
                            Salvar edição
                          </button>
                        </div>
                      </div>
                    ) : (
                      <p className="mt-3 text-sm text-slate-800 dark:text-slate-100 whitespace-pre-line">{comment.text}</p>
                    )}

                    {replyTo === comment.id && (
                      <div className="mt-3 bg-gray-50 dark:bg-slate-900 p-3 rounded-xl border border-gray-200 dark:border-slate-700">
                        <textarea
                          value={replyText}
                          onChange={(e) => setReplyText(e.target.value)}
                          placeholder="Escreva sua resposta..."
                          className="w-full p-2 border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                          rows={2}
                        />
                        <div className="flex justify-end gap-2 mt-2">
                          <button
                            type="button"
                            onClick={() => {
                              setReplyTo(null);
                              setReplyText("");
                            }}
                            className="px-3 py-1 text-sm font-semibold text-gray-600 dark:text-slate-300 rounded-lg hover:bg-gray-100 dark:hover:bg-slate-700"
                          >
                            Cancelar
                          </button>
                          <button
                            type="button"
                            onClick={() => handleReply(comment.id)}
                            className="px-3 py-1 bg-blue-600 text-white text-sm font-semibold rounded-lg hover:bg-blue-700"
                          >
                            Enviar
                          </button>
                        </div>
                      </div>
                    )}

                    {comment.replies?.length > 0 && (
                      <div className="mt-4 space-y-3">{renderCommentReplies(comment.replies)}</div>
                    )}

                    {/* ── Resposta oficial da empresa (aprovada) ── */}
                    {comment.companyReply && comment.companyReply.status === "approved" && (
                      <div className="mt-4 rounded-xl border-l-4 border-blue-600 dark:border-blue-400 bg-blue-50 dark:bg-blue-950/30 p-4">
                        <div className="flex items-center gap-2 mb-1">
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-blue-600 dark:text-blue-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                          </svg>
                          <span className="text-sm font-bold text-blue-700 dark:text-blue-300">Resposta oficial da empresa</span>
                        </div>
                        <p className="text-sm text-slate-800 dark:text-slate-100 whitespace-pre-line">{comment.companyReply.text}</p>
                        {comment.companyReply.publishedAt && (
                          <p className="text-xs text-slate-500 dark:text-slate-400 mt-2">
                            {new Date(comment.companyReply.publishedAt).toLocaleDateString("pt-BR", { day: "2-digit", month: "short", year: "numeric" })}
                          </p>
                        )}
                      </div>
                    )}

                    {/* ── Resposta pendente (visível apenas para o gestor) ── */}
                    {comment.companyReply && comment.companyReply.status === "pending" && isEmpresaPremium && (
                      <div className="mt-4 rounded-xl border border-amber-200 dark:border-amber-700 bg-amber-50 dark:bg-amber-900/20 p-3">
                        <p className="text-xs font-semibold text-amber-700 dark:text-amber-400 mb-1">Resposta enviada — aguardando aprovação da moderação</p>
                        <p className="text-sm text-slate-700 dark:text-slate-300 whitespace-pre-line">{comment.companyReply.text}</p>
                      </div>
                    )}

                    {/* ── Botão/Formulário de resposta da empresa ── */}
                    {isEmpresaPremium && !comment.companyReply && companyReplyTargetId !== comment.id && (
                      <div className="mt-3">
                        <button
                          type="button"
                          onClick={() => { setCompanyReplyTargetId(comment.id); setCompanyReplyText(""); }}
                          className="inline-flex items-center gap-1.5 text-xs font-bold text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 hover:underline transition"
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6" />
                          </svg>
                          Responder a esta avaliação
                        </button>
                      </div>
                    )}

                    {isEmpresaPremium && companyReplyTargetId === comment.id && (
                      <div className="mt-3 rounded-xl border border-blue-200 dark:border-blue-700 bg-blue-50/50 dark:bg-blue-950/20 p-4">
                        <p className="text-xs font-bold text-blue-700 dark:text-blue-300 mb-2">Resposta oficial da empresa</p>
                        <textarea
                          value={companyReplyText}
                          onChange={(e) => { if (e.target.value.length <= 500) setCompanyReplyText(e.target.value); }}
                          placeholder="Escreva a resposta oficial da empresa..."
                          className="w-full p-3 border border-blue-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                          rows={3}
                          maxLength={500}
                        />
                        <div className="flex items-center justify-between mt-2">
                          <span className="text-xs text-slate-400 dark:text-slate-500">{companyReplyText.length}/500</span>
                          <div className="flex gap-2">
                            <button
                              type="button"
                              onClick={() => { setCompanyReplyTargetId(null); setCompanyReplyText(""); }}
                              className="px-3 py-1.5 text-xs font-semibold text-slate-600 dark:text-slate-300 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700 transition"
                            >
                              Cancelar
                            </button>
                            <button
                              type="button"
                              onClick={() => handleCompanyReply(comment.id)}
                              disabled={!companyReplyText.trim() || companyReplySaving}
                              className="px-4 py-1.5 text-xs font-bold rounded-lg bg-blue-600 text-white hover:bg-blue-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              {companyReplySaving ? "Publicando…" : "Publicar resposta"}
                            </button>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                ))
            )}
          </div>
        </div>
      </div>
      </div>{/* /px-4 pt-6 */}

      {/* ── Assessoria Jurídica Trabalhista (Premium Trabalhador) ── */}
      {advogadosTrabalhistas.length > 0 && (
        <section className="max-w-5xl mx-auto px-4 py-10">
          <div className="bg-gradient-to-br from-purple-50 to-indigo-50 dark:from-purple-950/30 dark:to-indigo-950/30 rounded-2xl border border-purple-200 dark:border-purple-800 p-6 md:p-8">
            <div className="flex items-center gap-3 mb-1">
              <span className="text-2xl">⚖️</span>
              <h2 className="text-xl font-extrabold text-purple-800 dark:text-purple-200">Assessoria Jurídica Trabalhista</h2>
            </div>
            <p className="text-sm text-purple-600 dark:text-purple-400 mb-6">
              Sentiu que algo não foi justo? Advogados trabalhistas parceiros oferecem primeira consulta gratuita para assinantes dos planos Essencial e Premium.
            </p>

            {userIsPremium ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {advogadosTrabalhistas.map((a) => (
                  <div key={a.id} className="bg-white dark:bg-slate-800 rounded-xl border border-purple-100 dark:border-slate-700 p-4">
                    <div className="flex items-center gap-3 mb-2">
                      {a.foto ? (
                        <img src={a.foto} alt={a.nome} className="h-11 w-11 rounded-full object-cover border border-purple-200 dark:border-slate-600 shrink-0" />
                      ) : (
                        <span className="h-11 w-11 rounded-full bg-purple-100 dark:bg-slate-700 flex items-center justify-center text-lg shrink-0">⚖️</span>
                      )}
                      <div className="min-w-0 flex-1">
                        <h3 className="text-sm font-bold text-slate-800 dark:text-slate-100 truncate">{a.nome}</h3>
                        {a.oab && <p className="text-[10px] text-slate-500 dark:text-slate-400">OAB {a.oab}/{a.seccional}</p>}
                        {a.plano === "premium" && (
                          <span className="inline-block mt-0.5 px-2 py-0.5 text-[10px] font-bold bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-400 rounded-full">✓ Verificado</span>
                        )}
                      </div>
                    </div>
                    {a.descricao && <p className="text-xs text-slate-600 dark:text-slate-300 line-clamp-2 mb-2">{a.descricao}</p>}
                    {a.rating > 0 && (
                      <div className="flex items-center gap-1 mb-2">
                        <svg className="w-3.5 h-3.5 text-yellow-400" fill="currentColor" viewBox="0 0 20 20">
                          <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.286 3.957a1 1 0 00.95.69h4.162c.969 0 1.371 1.24.588 1.81l-3.37 2.448a1 1 0 00-.364 1.118l1.287 3.957c.3.921-.755 1.688-1.54 1.118l-3.37-2.448a1 1 0 00-1.175 0l-3.37 2.448c-.784.57-1.838-.197-1.54-1.118l1.287-3.957a1 1 0 00-.364-1.118L2.063 9.384c-.783-.57-.38-1.81.588-1.81h4.162a1 1 0 00.95-.69l1.286-3.957z" />
                        </svg>
                        <span className="text-xs text-slate-500 dark:text-slate-400">{a.rating.toFixed(1)} ({a.totalAvaliacoes || 0})</span>
                      </div>
                    )}
                    <Link to={`/apoiadores/perfil/${a.id}`} className="block w-full text-center px-3 py-2 rounded-lg bg-purple-600 text-white text-xs font-semibold hover:bg-purple-700 transition">
                      Consultar advogado
                    </Link>
                  </div>
                ))}
              </div>
            ) : (
              <div className="flex items-center gap-4 bg-white dark:bg-slate-800 rounded-xl border border-purple-200 dark:border-slate-700 p-5">
                <div className="text-3xl">🔒</div>
                <div className="flex-1 min-w-0">
                  <p className="font-bold text-purple-800 dark:text-purple-200 text-sm">Disponível para assinantes Essencial e Premium Trabalhador</p>
                  <p className="text-xs text-purple-600 dark:text-purple-400 mt-0.5">
                    Escolha o plano que melhor se adapta a você e ganhe acesso a {advogadosTrabalhistas.length} advogado{advogadosTrabalhistas.length > 1 ? "s" : ""} trabalhista{advogadosTrabalhistas.length > 1 ? "s" : ""} parceiro{advogadosTrabalhistas.length > 1 ? "s" : ""} com primeira consulta gratuita.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => navigate("/escolha-perfil?planos=1")}
                  className="shrink-0 px-4 py-2 rounded-lg bg-purple-600 text-white text-xs font-bold hover:bg-purple-700 transition"
                >
                  Ver planos
                </button>
              </div>
            )}

            <p className="text-[10px] text-purple-500 dark:text-purple-400 mt-4 text-center">
              A plataforma conecta você ao profissional — ela não presta serviço jurídico. Cada advogado é responsável pela conduta e atendimento conforme a OAB.
            </p>
          </div>
        </section>
      )}

      {/* ── Especialistas Recomendados ── */}
      {apoiadoresRecomendados.length > 0 && (
        <section className="max-w-5xl mx-auto px-4 py-10">
          <h2 className="text-xl font-extrabold text-slate-800 dark:text-slate-100 mb-1 text-center">Especialistas Recomendados</h2>
          <p className="text-sm text-slate-500 dark:text-slate-400 mb-6 text-center">Profissionais verificados prontos para ajudar.</p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {apoiadoresRecomendados.map((a) => (
              <div key={a.id} className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 p-4 flex items-center gap-3">
                {a.foto ? (
                  <img src={a.foto} alt={a.nome} className="h-12 w-12 rounded-full object-cover border border-slate-200 dark:border-slate-600 shrink-0" />
                ) : (
                  <span className="h-12 w-12 rounded-full bg-blue-100 dark:bg-slate-700 flex items-center justify-center text-xl shrink-0">👤</span>
                )}
                <div className="min-w-0 flex-1">
                  <h3 className="text-sm font-bold text-slate-800 dark:text-slate-100 truncate">{a.nome}</h3>
                  {a.especialidade && <p className="text-xs text-slate-500 dark:text-slate-400 truncate">{a.especialidade}</p>}
                  {a.rating > 0 && (
                    <span className="inline-flex items-center gap-1 mt-0.5">
                      <svg className="w-3.5 h-3.5 text-yellow-400" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.286 3.957a1 1 0 00.95.69h4.162c.969 0 1.371 1.24.588 1.81l-3.37 2.448a1 1 0 00-.364 1.118l1.287 3.957c.3.921-.755 1.688-1.54 1.118l-3.37-2.448a1 1 0 00-1.175 0l-3.37 2.448c-.784.57-1.838-.197-1.54-1.118l1.287-3.957a1 1 0 00-.364-1.118L2.063 9.384c-.783-.57-.38-1.81.588-1.81h4.162a1 1 0 00.95-.69l1.286-3.957z" />
                      </svg>
                      <span className="text-xs text-slate-500 dark:text-slate-400">{a.rating.toFixed(1)}</span>
                    </span>
                  )}
                </div>
                <Link to={`/apoiadores/perfil/${a.id}`} className="px-3 py-1.5 rounded-lg bg-blue-600 text-white text-xs font-semibold hover:bg-blue-700 transition shrink-0">
                  Ver perfil
                </Link>
              </div>
            ))}
          </div>
        </section>
      )}

        </>
      )}
    </div>
  );
}

export default CompanyDetails;
